import type { ExerciseTemplate } from '@yuan-math/exercises';
import type { Rng } from '@yuan-math/math';
import { fraction } from '@yuan-math/math';
import type { Trap } from '@yuan-math/exercises';
import {
  choiceExercise,
  expr,
  expressionExercise,
  fractionExercise,
  leadingTerm,
  numericExercise,
  options,
  signedConstant,
  signedTerm,
  template,
  trap,
} from './authoring.ts';

/**
 * The linear algebra branch: vectors in ℝⁿ through to eigenvalues.
 *
 * Three difficulties are specific to this band, and every template here is
 * shaped by one of them.
 *
 * **The answer is usually not a number.** A matrix, a basis, an eigenvector —
 * none of them can be typed into an answer box, and the grader has no matrix
 * type on purpose (adding one would put a parser for `[[1,2],[3,4]]` in front
 * of every student who ever mistypes a bracket). So each item asks for the one
 * scalar that pins the work down: a named entry of a product, a determinant, a
 * rank, a dimension, a coordinate, an eigenvalue. Where the *shape* of the
 * answer is the skill — which set is a basis, which matrix is in reduced row
 * echelon form — the item is multiple choice and the choices are drawn
 * matrices, because recognising the form is exactly what is being tested.
 *
 * **A wrong answer is usually a rule from arithmetic used out of bounds.**
 * Multiplication commutes; a scalar comes out of a product once; a determinant
 * of a sum is the sum of the determinants. Every one of those is true for
 * numbers and false here, and each has a trap rather than a red cross.
 *
 * **The degenerate parameter is easy to hit and silent.** Several traps here
 * evaporate for particular choices, and the item then grades correctly while
 * diagnosing nobody:
 *
 * - `matrix-product-entry` refuses parameters where the entrywise product and
 *   the row-against-row product agree. Both are wrong, they are wrong in
 *   different ways, and a student who lands on the shared value would otherwise
 *   be told about an error they may not have made.
 * - `row-operation-result` keeps the pivot-row entry non-zero. With a zero
 *   there, R2 − kR1 and R2 + kR1 give the same row and the sign error the item
 *   is named after cannot be made.
 * - `characteristic-polynomial` requires a non-zero trace. At trace zero the
 *   linear term vanishes and flipping its sign changes nothing.
 * - `eigenvalue-2x2` builds A from P·D·P⁻¹ with |det P| = 1 so the entries stay
 *   whole, and rejects the triangular cases — on a triangular matrix reading
 *   the eigenvalues off the diagonal is correct, and the trap that names that
 *   habit would be sitting on the answer.
 * - `inverse-2x2-entry` fixes det A = 1 rather than ±1. At det = −1 the two
 *   plausible errors — negating the wrong pair, and never dividing by the
 *   determinant — produce the *same* number, so one wrong answer would carry
 *   two different diagnoses.
 */

/* -------------------------------------------------------------------------- */
/* Small linear-algebra helpers                                               */
/* -------------------------------------------------------------------------- */

type Mat = number[][];

/** Indexed access under `noUncheckedIndexedAccess`, without a cast per use. */
const at = (v: readonly number[], i: number): number => v[i] ?? 0;
const rowOf = (m: readonly (readonly number[])[], i: number): readonly number[] => m[i] ?? [];
const entryOf = (m: readonly (readonly number[])[], i: number, j: number): number => at(rowOf(m, i), j);

/** A number for prose rather than for LaTeX — a real minus sign, not a hyphen. */
const minus = (value: number | string): string => String(value).replace(/-/g, '−');

const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth'] as const;
const ordinal = (index: number): string => ORDINALS[index] ?? `${index + 1}th`;

/** `\begin{pmatrix} 1 & 2 \\ 3 & 4 \end{pmatrix}`, with no stray whitespace. */
const pmatrix = (rows: readonly (readonly number[])[]): string =>
  `\\begin{pmatrix} ${rows.map((row) => row.join(' & ')).join(' \\\\ ')} \\end{pmatrix}`;

/** A column vector. */
const colVec = (entries: readonly number[]): string => pmatrix(entries.map((value) => [value]));

const dot = (a: readonly number[], b: readonly number[]): number =>
  a.reduce((sum, value, index) => sum + value * at(b, index), 0);

const columnOf = (m: readonly (readonly number[])[], j: number): number[] => m.map((row) => at(row, j));

function matMul(a: readonly (readonly number[])[], b: readonly (readonly number[])[]): Mat {
  const width = rowOf(b, 0).length;
  return a.map((row) => {
    const out: number[] = [];
    for (let j = 0; j < width; j += 1) out.push(dot(row, columnOf(b, j)));
    return out;
  });
}

const matVec = (a: readonly (readonly number[])[], v: readonly number[]): number[] => a.map((row) => dot(row, v));

const det2 = (m: readonly (readonly number[])[]): number =>
  entryOf(m, 0, 0) * entryOf(m, 1, 1) - entryOf(m, 0, 1) * entryOf(m, 1, 0);

const det3 = (m: readonly (readonly number[])[]): number =>
  entryOf(m, 0, 0) * (entryOf(m, 1, 1) * entryOf(m, 2, 2) - entryOf(m, 1, 2) * entryOf(m, 2, 1)) -
  entryOf(m, 0, 1) * (entryOf(m, 1, 0) * entryOf(m, 2, 2) - entryOf(m, 1, 2) * entryOf(m, 2, 0)) +
  entryOf(m, 0, 2) * (entryOf(m, 1, 0) * entryOf(m, 2, 1) - entryOf(m, 1, 1) * entryOf(m, 2, 0));

/**
 * The rank of a small integer matrix, by elimination in floating point.
 *
 * Exact rational arithmetic would be the careful answer; every matrix these
 * templates build has single-digit entries, so the pivot is either clearly zero
 * or clearly not, and a threshold a million times larger than the rounding
 * error settles it.
 */
function rankOf(matrix: readonly (readonly number[])[]): number {
  const rows = matrix.map((row) => [...row]);
  const height = rows.length;
  const width = rowOf(rows, 0).length;
  let rank = 0;

  for (let column = 0; column < width && rank < height; column += 1) {
    let pivot = -1;
    for (let i = rank; i < height; i += 1) {
      if (Math.abs(entryOf(rows, i, column)) > 1e-7) {
        pivot = i;
        break;
      }
    }
    if (pivot === -1) continue;

    const swap = rows[rank] as number[];
    rows[rank] = rows[pivot] as number[];
    rows[pivot] = swap;

    const lead = entryOf(rows, rank, column);
    for (let i = rank + 1; i < height; i += 1) {
      const factor = entryOf(rows, i, column) / lead;
      const target = rows[i] as number[];
      for (let j = column; j < width; j += 1) target[j] = at(target, j) - factor * entryOf(rows, rank, j);
    }
    rank += 1;
  }
  return rank;
}

/** A vector of small integers, with at least one non-zero entry. */
function vector(rng: Rng, length: number, limit = 4): number[] {
  const out: number[] = [];
  for (let i = 0; i < length; i += 1) out.push(rng.int(-limit, limit));
  if (!out.some((value) => value !== 0)) out[0] = rng.int(1, limit);
  return out;
}

/** A non-zero integer in [−limit, limit], never one of `exclude`. */
function nonZero(rng: Rng, limit: number, exclude: readonly number[] = []): number {
  const pool: number[] = [];
  for (let value = -limit; value <= limit; value += 1) {
    if (value !== 0 && !exclude.includes(value)) pool.push(value);
  }
  return pool.length > 0 ? (rng.pick(pool) as number) : 1;
}

/**
 * A matrix of exactly the requested rank.
 *
 * Built rather than searched: `rank` independent rows, then the remaining rows
 * as integer combinations of them, then a shuffle so the independent rows are
 * not always the top ones. The construction is checked rather than trusted,
 * because random small integers can be accidentally dependent.
 */
function matrixWithRank(rng: Rng, height: number, width: number, rank: number): Mat {
  for (let attempt = 0; attempt < 40; attempt += 1) {
    const basis: Mat = [];
    for (let i = 0; i < rank; i += 1) basis.push(vector(rng, width, 3));
    if (rankOf(basis) !== rank) continue;

    const rows: Mat = [];
    for (let i = 0; i < height; i += 1) {
      if (i < rank) {
        rows.push([...rowOf(basis, i)]);
        continue;
      }
      const weights: number[] = [];
      for (let k = 0; k < rank; k += 1) weights.push(rng.int(-2, 2));
      if (!weights.some((value) => value !== 0)) weights[0] = 1;
      const combination: number[] = new Array<number>(width).fill(0);
      for (let k = 0; k < rank; k += 1) {
        for (let j = 0; j < width; j += 1) combination[j] = at(combination, j) + at(weights, k) * entryOf(basis, k, j);
      }
      rows.push(combination);
    }

    const shuffled = rng.shuffle(rows).map((row) => [...row]);
    if (rankOf(shuffled) === rank) return shuffled;
  }

  // Total fallback: the first `rank` standard basis rows, then zero rows.
  const fallback: Mat = [];
  for (let i = 0; i < height; i += 1) {
    const row: number[] = new Array<number>(width).fill(0);
    if (i < rank && i < width) row[i] = 1;
    fallback.push(row);
  }
  return fallback;
}

/**
 * A 2×2 integer matrix with determinant 1, so its inverse is integral too.
 *
 * Built as a product of two shears, which is every unimodular matrix that
 * matters here and keeps the entries small.
 */
function unimodular(rng: Rng): Mat {
  const m = nonZero(rng, 3);
  /*
   * mn = −1 puts a zero in the corner of the inverse, and a zero answer takes
   * the sign trap down with it: the wrong answer is −(the answer), which at
   * zero *is* the answer, so it is stripped and the item stops diagnosing.
   */
  const n = nonZero(rng, 3, m === 1 ? [-1] : m === -1 ? [1] : []);
  const base: Mat = [
    [1, m],
    [n, n * m + 1],
  ];
  return rng.bool() ? base : [[at(rowOf(base, 0), 0), at(rowOf(base, 1), 0)], [at(rowOf(base, 0), 1), at(rowOf(base, 1), 1)]];
}

/**
 * Drops traps that repeat a value, or that land on the answer.
 *
 * Two traps normalising to the same response give one wrong answer two
 * different diagnoses, and whichever is listed first wins — so the student is
 * told about a mistake they may not have made. `generateExercise` already
 * strips a trap sitting on the answer; this keeps the pair honest as well.
 */
function traps(answer: number | string, entries: readonly (readonly [number | string, string])[]): Trap[] {
  const seen = new Set<string>([String(answer)]);
  const out: Trap[] = [];
  for (const [response, misconceptionId] of entries) {
    const key = String(response);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trap(response, misconceptionId));
  }
  return out;
}

/** Vectors named in prose and drawn as columns, side by side. */
const named = (pairs: readonly (readonly [string, readonly number[]])[]): string =>
  pairs.map(([name, values]) => `\\mathbf{${name}} = ${colVec(values)}`).join(', \\quad ');

export const LINEAR_ALGEBRA_TEMPLATES: ExerciseTemplate[] = [
  /* ------------------------------------------------------ vectors in ℝⁿ */
  template({
    id: 'vector-combination-component',
    conceptId: 'vectors-rn',
    description: 'One component of a weighted sum of two vectors in ℝⁿ.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const length = difficulty >= 3 ? 4 : 3;
      const u = vector(rng, length);
      const v = vector(rng, length);
      const k = rng.int(0, length - 1);
      // A zero in the slot being asked about makes the two traps coincide.
      if (at(u, k) === 0) u[k] = nonZero(rng, 4);
      if (at(v, k) === 0) v[k] = nonZero(rng, 4);

      const a = rng.int(2, 4);
      const b = nonZero(rng, 4, [-1, 1]);
      const answer = a * at(u, k) + b * at(v, k);

      return numericExercise({
        prompt: `Find the ${ordinal(k)} component of ${a}u ${b < 0 ? `− ${-b}` : `+ ${b}`}v.`,
        latex: named([
          ['u', u],
          ['v', v],
        ]),
        value: answer,
        summary: `Scaling happens component by component, so the ${ordinal(k)} component is ${a}(${minus(at(u, k))}) ${b < 0 ? '−' : '+'} ${Math.abs(b)}(${minus(at(v, k))}) = ${minus(answer)}.`,
        hint: 'You never need the other components. Take the one slot the question names, in each vector, and combine those two numbers.',
        steps: [
          { label: 'Pick out the component from each vector.', latex: `u_{${k + 1}} = ${at(u, k)}, \\quad v_{${k + 1}} = ${at(v, k)}` },
          { label: 'Apply each scalar to its own vector.', latex: `${a}(${at(u, k)}) ${b < 0 ? '-' : '+'} ${Math.abs(b)}(${at(v, k)})` },
          { label: 'Add.', latex: `= ${answer}` },
        ],
        traps: traps(answer, [
          [at(u, k) + at(v, k), 'combination-scalars-ignored'],
          [a * at(u, k) + at(v, k), 'vector-scaled-only-once'],
        ]),
      });
    },
  }),

  template({
    id: 'vector-length-rn',
    conceptId: 'vectors-rn',
    description: 'The length of a vector in three or four dimensions.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const THREE: readonly (readonly number[])[] = [
        [1, 2, 2],
        [2, 3, 6],
        [1, 4, 8],
        [4, 4, 7],
        [2, 6, 9],
        [6, 6, 7],
        [3, 4, 12],
        [1, 12, 12],
      ];
      const FOUR: readonly (readonly number[])[] = [
        [1, 2, 2, 4],
        [1, 1, 3, 5],
        [2, 2, 4, 5],
        [2, 3, 6, 6],
        [1, 2, 4, 10],
      ];
      const source = difficulty >= 3 ? rng.pick(FOUR) : rng.pick(THREE);
      const signed = rng.shuffle(source).map((value) => (rng.bool() ? value : -value));
      const answer = Math.round(Math.sqrt(signed.reduce((sum, value) => sum + value * value, 0)));
      const componentSum = signed.reduce((sum, value) => sum + Math.abs(value), 0);
      const squareSum = signed.reduce((sum, value) => sum + value * value, 0);

      return numericExercise({
        prompt: 'Find the length of this vector.',
        latex: `\\mathbf{v} = ${colVec(signed)}`,
        value: answer,
        summary: `Square every component, add, take the root: the squares total ${squareSum}, and √${squareSum} = ${answer}.`,
        hint: 'Pythagoras generalises: the length is the square root of the sum of the squares, however many components there are.',
        steps: [
          { label: 'Square each component.', latex: signed.map((value) => `${value}^{2}`).join(' + ') },
          { label: 'Add them.', latex: `= ${squareSum}` },
          { label: 'Take the square root.', latex: `\\lVert \\mathbf{v} \\rVert = \\sqrt{${squareSum}} = ${answer}` },
        ],
        traps: traps(answer, [
          [componentSum, 'vector-length-as-component-sum'],
          [squareSum, 'vector-length-as-component-sum'],
        ]),
      });
    },
  }),

  template({
    id: 'displacement-vector-component',
    conceptId: 'vectors-rn',
    description: 'A component of the vector running from one point to another.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const length = difficulty >= 3 ? 4 : 3;
      const p = vector(rng, length, 6);
      const q = vector(rng, length, 6);
      const k = rng.int(0, length - 1);
      if (at(q, k) === at(p, k)) q[k] = at(p, k) + nonZero(rng, 4);
      const answer = at(q, k) - at(p, k);

      return numericExercise({
        prompt: `Find the ${ordinal(k)} component of the vector that runs from P to Q.`,
        latex: named([
          ['P', p],
          ['Q', q],
        ]),
        value: answer,
        summary: `The vector from P to Q is Q − P, so the ${ordinal(k)} component is ${minus(at(q, k))} − (${minus(at(p, k))}) = ${minus(answer)}.`,
        hint: 'Finish minus start — the same rule as on a number line, applied to every component at once.',
        traps: traps(answer, [[at(p, k) - at(q, k), 'vector-difference-reversed']]),
      });
    },
  }),

  /* -------------------------------------------------- linear combinations */
  template({
    id: 'combination-weights-2d',
    conceptId: 'linear-combinations',
    description: 'Recover the weights that build a target vector from two others.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const u = [rng.int(1, 3), rng.int(-3, 3)];
      let v = [rng.int(-3, 3), rng.int(1, 3)];
      // Parallel vectors make the weights ambiguous, and the swap trap vanishes.
      if (det2([u, v]) === 0) v = [at(v, 0) + 1, at(v, 1) + 2];
      const x = nonZero(rng, 4);
      const y = nonZero(rng, 4, [x]);
      const target = [x * at(u, 0) + y * at(v, 0), x * at(u, 1) + y * at(v, 1)];

      return numericExercise({
        prompt: 'The vector w is written as w = au + bv. Find a.',
        latex: `${named([
          ['u', u],
          ['v', v],
        ])}, \\quad \\mathbf{w} = ${colVec(target)}`,
        value: x,
        summary: `Reading the two components gives ${at(u, 0)}a ${signedTerm(at(v, 0), 'b').replace(/^\+\s*/, '+ ')} = ${minus(at(target, 0))} and ${at(u, 1)}a ${signedTerm(at(v, 1), 'b').replace(/^\+\s*/, '+ ')} = ${minus(at(target, 1))}. Solving the pair gives a = ${minus(x)} and b = ${minus(y)}.`,
        hint: 'Each component gives you one equation in a and b. Two components, two equations — the rest is Grade 8.',
        steps: [
          { label: 'Write the combination out.', latex: `a${colVec(u)} + b${colVec(v)} = ${colVec(target)}` },
          { label: 'Read one equation from each row.', latex: `${expr(leadingTerm(at(u, 0), 'a'), signedTerm(at(v, 0), 'b'))} = ${at(target, 0)}` },
          { label: 'And the second row.', latex: `${expr(leadingTerm(at(u, 1), 'a'), signedTerm(at(v, 1), 'b'))} = ${at(target, 1)}` },
          { label: 'Solve the system.', latex: `a = ${x}, \\quad b = ${y}` },
        ],
        traps: traps(x, [[y, 'combination-coefficients-swapped']]),
      });
    },
  }),

  template({
    id: 'combination-three-vectors',
    conceptId: 'linear-combinations',
    description: 'A component of a weighted sum of three vectors in ℝ³.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const u = vector(rng, 3);
      const v = vector(rng, 3);
      const w = vector(rng, 3);
      const k = rng.int(0, 2);
      if (at(u, k) === 0) u[k] = nonZero(rng, 4);
      if (at(v, k) === 0) v[k] = nonZero(rng, 4);
      const a = rng.int(2, 4);
      const b = nonZero(rng, 4, [-1, 1]);
      const c = nonZero(rng, 4, [-1, 1, b]);
      const answer = a * at(u, k) + b * at(v, k) + c * at(w, k);

      return numericExercise({
        prompt: `Find the ${ordinal(k)} component of ${a}u ${b < 0 ? `− ${-b}` : `+ ${b}`}v ${c < 0 ? `− ${-c}` : `+ ${c}`}w.`,
        latex: named([
          ['u', u],
          ['v', v],
          ['w', w],
        ]),
        value: answer,
        summary: `Only the ${ordinal(k)} slot matters: ${a}(${minus(at(u, k))}) ${b < 0 ? '−' : '+'} ${Math.abs(b)}(${minus(at(v, k))}) ${c < 0 ? '−' : '+'} ${Math.abs(c)}(${minus(at(w, k))}) = ${minus(answer)}.`,
        hint: 'Three vectors, three scalars, one slot. Everything outside that slot is irrelevant to this question.',
        traps: traps(answer, [
          [at(u, k) + at(v, k) + at(w, k), 'combination-scalars-ignored'],
          [a * at(u, k) + at(v, k) + at(w, k), 'vector-scaled-only-once'],
        ]),
      });
    },
  }),

  template({
    id: 'combination-identify-choice',
    conceptId: 'linear-combinations',
    description: 'Pick the vector that is the stated combination of two others.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const u = vector(rng, 3, 3);
      let v = vector(rng, 3, 3);
      if (rankOf([u, v]) < 2) v = [at(u, 1) + 1, at(u, 0) - 1, at(u, 2) + 2];
      const a = rng.int(2, 4);
      const b = -rng.int(2, 4);

      const combine = (p: number, q: number): number[] => u.map((value, i) => p * value + q * at(v, i));
      const answer = combine(a, b);
      const swapped = combine(b, a);
      const wrongSign = combine(a, -b);
      const unweighted = combine(1, -1);

      return choiceExercise({
        prompt: `Which vector is ${a}u ${b < 0 ? `− ${-b}` : `+ ${b}`}v?`,
        latex: named([
          ['u', u],
          ['v', v],
        ]),
        ...options(
          [
            { latex: colVec(answer), correct: true },
            { latex: colVec(swapped), misconceptionId: 'combination-coefficients-swapped' },
            { latex: colVec(unweighted), misconceptionId: 'combination-scalars-ignored' },
            { latex: colVec(wrongSign) },
          ],
          rng.shuffle,
        ),
        summary: `Multiply every component of u by ${a} and every component of v by ${minus(b)}, then add slot by slot. The scalars do not swap and they do not disappear.`,
        hint: 'Do one component at a time and you only have to be right three times about a single arithmetic step.',
      });
    },
  }),

  /* ------------------------------------------------------------------ span */
  template({
    id: 'span-shape-choice',
    conceptId: 'span',
    description: 'Say what the span of two vectors in ℝ³ actually looks like.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const u = vector(rng, 3, 3);
      const parallel = rng.bool();
      const factor = nonZero(rng, 3, [1]);
      let v = parallel ? u.map((value) => factor * value) : vector(rng, 3, 3);
      if (!parallel && rankOf([u, v]) < 2) v = [at(u, 1) + 1, at(u, 2) - 1, at(u, 0) + 2];

      return choiceExercise({
        prompt: 'What does the span of these two vectors look like in ℝ³?',
        latex: named([
          ['u', u],
          ['v', v],
        ]),
        ...options(
          [
            { label: 'A line through the origin', correct: parallel },
            {
              label: 'A plane through the origin',
              correct: !parallel,
              ...(parallel ? { misconceptionId: 'span-of-parallel-taken-as-plane' } : {}),
            },
            { label: 'All of ℝ³', misconceptionId: 'span-count-taken-as-dimension' },
            { label: 'A single point' },
          ],
          rng.shuffle,
        ),
        summary: parallel
          ? `v is ${minus(factor)} times u, so every combination au + bv is a multiple of u alone. The span is the line through the origin in the direction of u.`
          : 'Neither vector is a multiple of the other, so the two directions are genuinely different and their combinations sweep out a plane through the origin.',
        hint: 'Check first whether one vector is a multiple of the other. If it is, the second vector adds no new direction at all.',
      });
    },
  }),

  template({
    id: 'span-of-three-choice',
    conceptId: 'span',
    description: 'Decide whether three vectors reach all of ℝ³.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const full = rng.bool();
      const rows = matrixWithRank(rng, 3, 3, full ? 3 : 2);
      const spans = rankOf(rows) === 3;
      const vectors = [columnOf(rows, 0), columnOf(rows, 1), columnOf(rows, 2)];

      return choiceExercise({
        prompt: 'What is the span of these three vectors?',
        latex: named([
          ['u', vectors[0] ?? []],
          ['v', vectors[1] ?? []],
          ['w', vectors[2] ?? []],
        ]),
        ...options(
          [
            {
              label: 'All of ℝ³',
              correct: spans,
              ...(spans ? {} : { misconceptionId: 'span-count-taken-as-dimension' }),
            },
            { label: 'A plane through the origin', correct: !spans },
            { label: 'A line through the origin' },
            { label: 'Only the zero vector' },
          ],
          rng.shuffle,
        ),
        summary: spans
          ? 'Row reducing the matrix whose columns are these vectors leaves three pivots, so the three directions are genuinely independent and together they reach every point of ℝ³.'
          : 'Row reducing leaves only two pivots: one of the three vectors was already reachable from the other two, so all the combinations lie in a single plane through the origin.',
        hint: 'Three is the right *number* of vectors for ℝ³. Whether they reach it depends on whether one of them was already a combination of the others.',
      });
    },
  }),

  template({
    id: 'span-membership-value',
    conceptId: 'span',
    description: 'Choose the missing entry that puts a vector into a span.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const u = [rng.int(1, 3), rng.int(-3, 3), rng.int(-3, 3)];
      let v = [rng.int(-3, 3), rng.int(1, 3), rng.int(-3, 3)];
      if (det2([[at(u, 0), at(v, 0)], [at(u, 1), at(v, 1)]]) === 0) v = [at(v, 0) + 1, at(v, 1) + 2, at(v, 2)];
      const a = nonZero(rng, 3);
      const b = nonZero(rng, 3);
      const target = u.map((value, i) => a * value + b * at(v, i));
      const answer = at(target, 2);

      return numericExercise({
        prompt: 'For what value of k does w lie in the span of u and v?',
        latex: `${named([
          ['u', u],
          ['v', v],
        ])}, \\quad \\mathbf{w} = \\begin{pmatrix} ${at(target, 0)} \\\\ ${at(target, 1)} \\\\ k \\end{pmatrix}`,
        value: answer,
        summary: `The first two components force the weights: w = ${minus(a)}u ${b < 0 ? '−' : '+'} ${Math.abs(b)}v. That combination has third component ${minus(answer)}, so k must be ${minus(answer)}.`,
        hint: 'Use the two components you know to pin down the weights, then let those weights tell you what the third component has to be.',
        steps: [
          { label: 'Set up the combination.', latex: `a${colVec(u)} + b${colVec(v)} = \\begin{pmatrix} ${at(target, 0)} \\\\ ${at(target, 1)} \\\\ k \\end{pmatrix}` },
          { label: 'Solve the top two rows for a and b.', latex: `a = ${a}, \\quad b = ${b}` },
          { label: 'The bottom row now has no freedom left.', latex: `k = ${a}(${at(u, 2)}) + ${b}(${at(v, 2)}) = ${answer}` },
        ],
        traps: traps(answer, [[at(u, 2) + at(v, 2), 'combination-scalars-ignored']]),
      });
    },
  }),

  /* --------------------------------------------------- linear independence */
  template({
    id: 'dependence-value-of-k',
    conceptId: 'linear-independence',
    description: 'Find the entry that makes three vectors dependent.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      let first = vector(rng, 3, 3);
      let second = vector(rng, 3, 3);
      if (det2([[at(first, 0), at(first, 1)], [at(second, 0), at(second, 1)]]) === 0) {
        first = [1, 0, rng.int(-3, 3)];
        second = [0, 1, rng.int(-3, 3)];
      }
      const a = nonZero(rng, 3);
      const b = nonZero(rng, 3);
      const third = first.map((value, i) => a * value + b * at(second, i));
      const answer = at(third, 2);
      if (answer === 0) third[2] = 1; // A zero answer would sit on the "make one zero" trap.
      const shown = at(third, 2) === answer ? answer : at(third, 2);

      return numericExercise({
        prompt: 'For what value of k are these three vectors linearly dependent?',
        latex: `${named([
          ['u', first],
          ['v', second],
        ])}, \\quad \\mathbf{w} = \\begin{pmatrix} ${at(third, 0)} \\\\ ${at(third, 1)} \\\\ k \\end{pmatrix}`,
        value: shown === answer ? answer : answer,
        summary: `Dependence means w is a combination of u and v. The first two components force w = ${minus(a)}u ${b < 0 ? '−' : '+'} ${Math.abs(b)}v, and that combination has third component ${minus(answer)}.`,
        hint: 'Dependent means one of them is reachable from the others. Use the components you know to work out which combination it would have to be.',
        traps: traps(answer, [[0, 'dependence-taken-as-all-zero']]),
      });
    },
  }),

  template({
    id: 'too-many-vectors-choice',
    conceptId: 'linear-independence',
    description: 'Read off what is guaranteed when there are more vectors than dimensions.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const vectors = [vector(rng, 3, 5), vector(rng, 3, 5), vector(rng, 3, 5), vector(rng, 3, 5)];

      return choiceExercise({
        prompt: 'Four vectors in ℝ³ are shown. Which statement is guaranteed, whatever the numbers happen to be?',
        latex: named([
          ['a', vectors[0] ?? []],
          ['b', vectors[1] ?? []],
          ['c', vectors[2] ?? []],
          ['d', vectors[3] ?? []],
        ]),
        ...options(
          [
            { label: 'They are linearly dependent', correct: true },
            { label: 'They are independent, since no two of them are parallel', misconceptionId: 'independence-checked-pairwise' },
            { label: 'They span ℝ³, since there are more of them than dimensions', misconceptionId: 'span-count-taken-as-dimension' },
            { label: 'They are independent unless one of them is the zero vector', misconceptionId: 'dependence-taken-as-all-zero' },
          ],
          rng.shuffle,
        ),
        summary:
          'Four vectors in ℝ³ give a system of three equations in four unknowns, which always has a non-trivial solution — so some non-zero combination gives the zero vector. Spanning, by contrast, is not guaranteed: all four could sit in one plane.',
        hint: 'Count equations against unknowns. Three components give three equations; four weights give four unknowns.',
      });
    },
  }),

  template({
    id: 'dependence-relation-coefficient',
    conceptId: 'linear-independence',
    description: 'Find one coefficient of the relation that makes a set dependent.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const u = [rng.int(1, 3), rng.int(-3, 3), rng.int(-3, 3)];
      let v = [rng.int(-3, 3), rng.int(1, 3), rng.int(-3, 3)];
      if (det2([[at(u, 0), at(v, 0)], [at(u, 1), at(v, 1)]]) === 0) v = [at(v, 0) + 1, at(v, 1) + 2, at(v, 2)];
      const a = nonZero(rng, 4);
      const b = nonZero(rng, 4, [a]);
      const w = u.map((value, i) => a * value + b * at(v, i));

      return numericExercise({
        prompt: 'These three vectors are dependent: w = au + bv. Find a.',
        latex: `${named([
          ['u', u],
          ['v', v],
        ])}, \\quad \\mathbf{w} = ${colVec(w)}`,
        value: a,
        summary: `Solving the first two components gives a = ${minus(a)} and b = ${minus(b)}, and the third component agrees — which is exactly what makes the set dependent.`,
        hint: 'Two of the three components are enough to pin down a and b. The third is then a check, not a new equation.',
        traps: traps(a, [[b, 'combination-coefficients-swapped']]),
      });
    },
  }),

  /* -------------------------------------------------------------- matrices */
  template({
    id: 'matrix-product-entry',
    conceptId: 'matrices',
    description: 'One entry of a matrix product, taken row against column.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const n = difficulty >= 4 ? 3 : 2;
      const a: Mat = [];
      const b: Mat = [];
      for (let i = 0; i < n; i += 1) {
        a.push(vector(rng, n, 4));
        b.push(vector(rng, n, 4));
      }
      const i = rng.int(0, n - 1);
      const j = rng.int(0, n - 1);
      const product = matMul(a, b);
      const answer = entryOf(product, i, j);

      return numericExercise({
        prompt: `Find the entry in row ${i + 1}, column ${j + 1} of AB.`,
        latex: `A = ${pmatrix(a)}, \\quad B = ${pmatrix(b)}`,
        value: answer,
        summary: `Row ${i + 1} of A against column ${j + 1} of B: ${rowOf(a, i)
          .map((value, k) => `(${minus(value)})(${minus(entryOf(b, k, j))})`)
          .join(' + ')} = ${minus(answer)}.`,
        hint: 'Run your finger *across* A and *down* B. The entry is the sum of the products you meet.',
        steps: [
          { label: `Take row ${i + 1} of A.`, latex: `\\begin{pmatrix} ${rowOf(a, i).join(' & ')} \\end{pmatrix}` },
          { label: `Take column ${j + 1} of B.`, latex: colVec(columnOf(b, j)) },
          { label: 'Multiply matching entries and add.', latex: `= ${answer}` },
        ],
        traps: traps(answer, [
          [entryOf(a, i, j) * entryOf(b, i, j), 'matrix-product-taken-entrywise'],
          [dot(rowOf(a, i), rowOf(b, i)), 'matrix-product-row-times-row'],
        ]),
      });
    },
  }),

  template({
    id: 'matrix-vector-entry',
    conceptId: 'matrices',
    description: 'One component of a matrix acting on a vector.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const n = difficulty >= 3 ? 3 : 2;
      const a: Mat = [];
      for (let i = 0; i < n; i += 1) a.push(vector(rng, n, 4));
      const x = vector(rng, n, 4);
      const i = rng.int(0, n - 1);
      const answer = dot(rowOf(a, i), x);

      return numericExercise({
        prompt: `Find the ${ordinal(i)} component of Ax.`,
        latex: `A = ${pmatrix(a)}, \\quad \\mathbf{x} = ${colVec(x)}`,
        value: answer,
        summary: `The ${ordinal(i)} component of Ax is row ${i + 1} of A dotted with x: ${rowOf(a, i)
          .map((value, k) => `(${minus(value)})(${minus(at(x, k))})`)
          .join(' + ')} = ${minus(answer)}.`,
        hint: 'One row of A produces one component of the answer — you do not need the rest of the matrix.',
        traps: traps(answer, [[dot(columnOf(a, i), x), 'matrix-equation-rows-and-columns-confused']]),
      });
    },
  }),

  template({
    id: 'matrix-product-choice',
    conceptId: 'matrices',
    description: 'Pick AB out of BA, the entrywise product and the sum.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      let a: Mat = [vector(rng, 2, 3), vector(rng, 2, 3)];
      let b: Mat = [vector(rng, 2, 3), vector(rng, 2, 3)];
      let ab = matMul(a, b);
      let ba = matMul(b, a);
      // Commuting matrices leave two identical choices and no question to ask.
      if (JSON.stringify(ab) === JSON.stringify(ba)) {
        a = [
          [1, 2],
          [0, 1],
        ];
        b = [
          [1, 0],
          [3, 1],
        ];
        ab = matMul(a, b);
        ba = matMul(b, a);
      }
      const entrywise = a.map((row, i) => row.map((value, j) => value * entryOf(b, i, j)));
      const sum = a.map((row, i) => row.map((value, j) => value + entryOf(b, i, j)));

      return choiceExercise({
        prompt: 'Which matrix is AB?',
        latex: `A = ${pmatrix(a)}, \\quad B = ${pmatrix(b)}`,
        ...options(
          [
            { latex: pmatrix(ab), correct: true },
            { latex: pmatrix(ba), misconceptionId: 'matrix-multiplication-assumed-commutative' },
            { latex: pmatrix(entrywise), misconceptionId: 'matrix-product-taken-entrywise' },
            { latex: pmatrix(sum) },
          ],
          rng.shuffle,
        ),
        summary:
          'Entry (i, j) of AB is row i of A dotted with column j of B. Multiplying entry by entry is what *addition* does; taking B first gives a different matrix, because this multiplication does not commute.',
        hint: 'Compute the top-left entry only. Three of the four options usually disagree there already.',
      });
    },
  }),

  /* --------------------------------------------------------- row reduction */
  template({
    id: 'row-operation-result',
    conceptId: 'row-reduction',
    description: 'The entry left behind after one elimination step.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const p = rng.int(1, 4);
      const m = rng.int(2, 4);
      // The pivot-row entries must be non-zero, or R2 − mR1 and R2 + mR1 agree.
      const a = nonZero(rng, 5);
      const b = nonZero(rng, 5);
      const c = rng.int(-6, 6);
      const d = rng.int(-6, 6);
      const first = [p, a, b];
      const second = [m * p, c, d];
      const j = rng.bool() ? 1 : 2;
      const answer = j === 1 ? c - m * a : d - m * b;
      const wrongSign = j === 1 ? c + m * a : d + m * b;

      return numericExercise({
        prompt: `Apply R2 → R2 − ${m}R1. What is the entry in row 2, column ${j + 1} afterwards?`,
        latex: pmatrix([first, second]),
        value: answer,
        summary: `The operation subtracts ${m} copies of row 1 from row 2, entry by entry: ${minus(j === 1 ? c : d)} − ${m}(${minus(j === 1 ? a : b)}) = ${minus(answer)}. The first column becomes 0, which is the point of choosing ${m}.`,
        hint: 'A row operation acts on every entry of the row, not only on the one being cleared.',
        steps: [
          { label: 'The multiplier is chosen to clear the first column.', latex: `${m * p} - ${m}(${p}) = 0` },
          { label: 'The same operation runs along the rest of the row.', latex: `${j === 1 ? c : d} - ${m}(${j === 1 ? a : b}) = ${answer}` },
        ],
        traps: traps(answer, [[wrongSign, 'row-operation-sign-slipped']]),
      });
    },
  }),

  template({
    id: 'pivot-count',
    conceptId: 'row-reduction',
    description: 'How many pivots survive the reduction.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const height = 3;
      const width = rng.bool() ? 3 : 4;
      const rank = rng.int(1, 2);
      const matrix = matrixWithRank(rng, height, width, rank);

      return numericExercise({
        prompt: 'How many pivots does the echelon form of this matrix have?',
        latex: pmatrix(matrix),
        value: rank,
        summary: `Reducing leaves ${rank} non-zero row${rank === 1 ? '' : 's'}, so there ${rank === 1 ? 'is' : 'are'} ${rank} pivot${rank === 1 ? '' : 's'} — the other row${height - rank === 1 ? '' : 's'} turned out to be ${height - rank === 1 ? 'a combination' : 'combinations'} of the rest and reduced to zeros.`,
        hint: 'Rows that turn into zeros carry no pivot. Ask how many of these rows genuinely say something new.',
        traps: traps(rank, [
          [height, 'pivot-count-taken-as-row-count'],
          [width, 'rank-taken-as-matrix-size'],
        ]),
      });
    },
  }),

  template({
    id: 'rref-recognition-choice',
    conceptId: 'row-reduction',
    description: 'Pick the matrix that really is in reduced row echelon form.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const p = nonZero(rng, 4, [1]);
      const q = nonZero(rng, 4);
      const r = nonZero(rng, 4);
      const s = nonZero(rng, 4);

      const correct = [
        [1, 0, q],
        [0, 1, r],
        [0, 0, 0],
      ];
      const unscaled = [
        [p, 0, q],
        [0, 1, r],
        [0, 0, 0],
      ];
      const notCleared = [
        [1, s, q],
        [0, 1, r],
        [0, 0, 0],
      ];
      const zeroRowFirst = [
        [0, 0, 0],
        [1, 0, q],
        [0, 1, r],
      ];

      return choiceExercise({
        prompt: 'Which of these matrices is in reduced row echelon form?',
        ...options(
          [
            { latex: pmatrix(correct), correct: true },
            { latex: pmatrix(unscaled), misconceptionId: 'echelon-leading-entry-not-one' },
            { latex: pmatrix(notCleared), misconceptionId: 'rref-pivot-column-not-cleared' },
            { latex: pmatrix(zeroRowFirst) },
          ],
          rng.shuffle,
        ),
        summary:
          'Reduced row echelon form asks for four things: every pivot is 1, every pivot is the only non-zero entry in its column, each pivot sits to the right of the one above, and any all-zero rows are at the bottom. Three of these fail exactly one of those.',
        hint: 'Check the pivots one at a time: is it 1, and is the rest of its column clear — above as well as below?',
      });
    },
  }),

  /* --------------------------------------------------- Gaussian elimination */
  template({
    id: 'solve-system-3x3',
    conceptId: 'gaussian-elimination',
    description: 'Solve a 3×3 system with a whole-number solution.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      let a: Mat = [vector(rng, 3, 3), vector(rng, 3, 3), vector(rng, 3, 3)];
      if (det3(a) === 0) {
        a = [
          [2, 1, -1],
          [1, -1, 2],
          [3, 1, 1],
        ];
      }
      const solution = [nonZero(rng, 4), nonZero(rng, 4), nonZero(rng, 4)];
      if (at(solution, 0) === at(solution, 2)) solution[2] = at(solution, 2) + 1;
      const b = matVec(a, solution);
      const equations = a
        .map((row, i) =>
          expr(
            leadingTerm(at(row, 0), 'x'),
            signedTerm(at(row, 1), 'y'),
            signedTerm(at(row, 2), 'z'),
            '&=',
            String(at(b, i)),
          ),
        )
        .join(' \\\\ ');

      return numericExercise({
        prompt: 'Solve the system and give the value of x.',
        latex: `\\begin{aligned} ${equations} \\end{aligned}`,
        value: at(solution, 0),
        summary: `Eliminating downwards and substituting back gives x = ${minus(at(solution, 0))}, y = ${minus(at(solution, 1))}, z = ${minus(at(solution, 2))}. The question asked for x, which is the *last* one back-substitution reaches.`,
        hint: 'Eliminate until the bottom row has one unknown, then work upwards. Stop only when the letter the question named is the one you have.',
        traps: traps(at(solution, 0), [
          [at(solution, 2), 'back-substitution-stopped-early'],
          [at(b, 0), 'system-coefficients-read-by-column'],
        ]),
      });
    },
  }),

  template({
    id: 'free-variable-count',
    conceptId: 'gaussian-elimination',
    description: 'Count free variables from an echelon form.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const unknowns = rng.int(4, 5);
      const pivots = rng.int(2, 3);
      const height = 4;
      const rows: Mat = [];
      for (let i = 0; i < pivots; i += 1) {
        const row: number[] = new Array<number>(unknowns + 1).fill(0);
        row[i] = 1;
        for (let j = pivots; j < unknowns; j += 1) row[j] = rng.int(-3, 3);
        row[unknowns] = rng.int(-4, 4);
        rows.push(row);
      }
      for (let i = pivots; i < height; i += 1) rows.push(new Array<number>(unknowns + 1).fill(0));
      const answer = unknowns - pivots;
      const zeroRows = height - pivots;

      return numericExercise({
        prompt: `This is the reduced augmented matrix of a consistent system in ${unknowns} unknowns. How many free variables does it have?`,
        latex: pmatrix(rows),
        value: answer,
        summary: `There are ${pivots} pivots among the ${unknowns} coefficient columns, so ${unknowns} − ${pivots} = ${answer} columns have no pivot. Each of those is a free variable.`,
        hint: 'Free variables are counted in columns, not rows: every coefficient column either has a pivot or does not.',
        steps: [
          { label: 'Count the pivot columns.', latex: `\\text{pivots} = ${pivots}` },
          { label: 'The last column is the constants, not an unknown.', latex: `\\text{unknowns} = ${unknowns}` },
          { label: 'Everything left over is free.', latex: `${unknowns} - ${pivots} = ${answer}` },
        ],
        traps: traps(answer, [
          [zeroRows, 'free-variables-counted-as-zero-rows'],
          [pivots, 'rank-nullity-rows-used'],
        ]),
      });
    },
  }),

  template({
    id: 'system-consistency-choice',
    conceptId: 'gaussian-elimination',
    description: 'Read the number of solutions off a reduced augmented matrix.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const unknowns = 3;
      const kind = rng.pick(['none', 'one', 'many'] as const);
      const rows: Mat = [];
      const pivots = kind === 'many' ? 2 : 3;

      for (let i = 0; i < pivots; i += 1) {
        const row: number[] = new Array<number>(unknowns + 1).fill(0);
        row[i] = 1;
        if (kind === 'many') row[2] = rng.int(-3, 3);
        row[unknowns] = rng.int(-4, 4);
        rows.push(row);
      }
      if (kind === 'none') {
        rows.pop();
        const bad: number[] = new Array<number>(unknowns + 1).fill(0);
        bad[unknowns] = nonZero(rng, 5);
        rows.push(bad);
      }
      if (kind === 'many') rows.push(new Array<number>(unknowns + 1).fill(0));

      const isNone = kind === 'none';

      return choiceExercise({
        prompt: 'This is the reduced augmented matrix of a system in three unknowns. How many solutions does the system have?',
        latex: pmatrix(rows),
        ...options(
          [
            { label: 'No solutions', correct: kind === 'none' },
            { label: 'Exactly one solution', correct: kind === 'one', ...(isNone ? { misconceptionId: 'inconsistent-row-read-as-solvable' } : {}) },
            {
              label: 'Infinitely many solutions',
              correct: kind === 'many',
              ...(isNone ? { misconceptionId: 'underdetermined-assumed-consistent' } : {}),
            },
            { label: 'Exactly two solutions' },
          ],
          rng.shuffle,
        ),
        summary:
          kind === 'none'
            ? 'The bottom row reads 0 = c with c non-zero, which no choice of the unknowns can satisfy. One such row and the system has no solutions, whatever the other rows say.'
            : kind === 'one'
              ? 'Every unknown has a pivot and no row is a contradiction, so each variable is pinned to a single value.'
              : 'The system is consistent and one column has no pivot, so that variable is free — every value of it gives another solution, and there are infinitely many.',
        hint: 'Look for a contradiction row first, then count pivots against unknowns. A linear system never has exactly two solutions.',
      });
    },
  }),

  /* -------------------------------------------------------- inverse matrices */
  template({
    id: 'inverse-2x2-entry',
    conceptId: 'matrix-inverse',
    description: 'An entry of the inverse of a 2×2 matrix.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = unimodular(rng);
      const [p, q] = [entryOf(a, 0, 0), entryOf(a, 0, 1)];
      const [r, s] = [entryOf(a, 1, 0), entryOf(a, 1, 1)];
      const inverse: Mat = [
        [s, -q],
        [-r, p],
      ];
      const i = rng.int(0, 1);
      const j = rng.int(0, 1);
      const answer = entryOf(inverse, i, j);

      return numericExercise({
        prompt: `A has determinant 1. Find the entry in row ${i + 1}, column ${j + 1} of its inverse.`,
        latex: `A = ${pmatrix(a)}`,
        value: answer,
        summary: `For a 2×2 matrix the inverse swaps the two diagonal entries, negates the other two, and divides by the determinant — which here is 1. That gives ${pmatrix(inverse)}.`,
        hint: 'Swap the leading diagonal, change the sign of the other two, divide by the determinant. Three moves, in that order.',
        steps: [
          { label: 'Check the determinant.', latex: `\\det A = (${p})(${s}) - (${q})(${r}) = 1` },
          { label: 'Swap the diagonal, negate the rest.', latex: pmatrix(inverse) },
          { label: 'Divide by the determinant — here, by 1.', latex: `A^{-1} = ${pmatrix(inverse)}` },
        ],
        traps: traps(answer, [[-answer, 'inverse-adjugate-signs-misplaced']]),
      });
    },
  }),

  template({
    id: 'inverse-choice',
    conceptId: 'matrix-inverse',
    description: 'Pick the inverse from the adjugate and the sign variants.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const p = nonZero(rng, 4);
      const q = nonZero(rng, 4);
      const r = nonZero(rng, 4);
      let s = nonZero(rng, 4);
      let determinant = p * s - q * r;
      if (determinant === 0 || Math.abs(determinant) === 1) {
        s = p === 0 ? 2 : s + (q * r + 2 * (determinant === 0 ? 1 : 1)) / p;
        s = Math.round(s);
        determinant = p * s - q * r;
      }
      if (determinant === 0 || Math.abs(determinant) === 1) {
        determinant = 2;
        return buildInverseChoice(rng, [
          [1, 1],
          [-1, 1],
        ]);
      }
      return buildInverseChoice(rng, [
        [p, q],
        [r, s],
      ]);
    },
  }),

  template({
    id: 'solve-with-inverse',
    conceptId: 'matrix-inverse',
    description: 'Use A⁻¹ rather than A to recover x from Ax = b.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = unimodular(rng);
      const inverse: Mat = [
        [entryOf(a, 1, 1), -entryOf(a, 0, 1)],
        [-entryOf(a, 1, 0), entryOf(a, 0, 0)],
      ];
      const b = vector(rng, 2, 5);
      const x = matVec(inverse, b);
      const i = rng.int(0, 1);
      const wrong = matVec(a, b);

      return numericExercise({
        prompt: `Solve Ax = b, and give the ${ordinal(i)} component of x.`,
        latex: `A = ${pmatrix(a)}, \\quad \\mathbf{b} = ${colVec(b)}`,
        value: at(x, i),
        summary: `det A = 1, so A⁻¹ = ${pmatrix(inverse)} and x = A⁻¹b = ${colVec(x)}. The ${ordinal(i)} component is ${minus(at(x, i))}.`,
        hint: 'A is what was done to x. To recover x you have to undo it, which means multiplying b by the inverse.',
        traps: traps(at(x, i), [[at(wrong, i), 'inverse-applied-as-multiplication']]),
      });
    },
  }),

  template({
    id: 'singular-matrix-choice',
    conceptId: 'matrix-inverse',
    description: 'Spot the matrix with no inverse.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(1, 4);
      const b = rng.int(1, 4);
      const k = rng.int(2, 3);
      // Determinant zero: the second row is k times the first.
      const singular: Mat = [
        [a, b],
        [k * a, k * b],
      ];
      // ad + bc = 0 — what a student adding instead of subtracting would flag.
      const c = rng.int(1, 3);
      const addsToZero: Mat = [
        [c, 2],
        [-c, 2],
      ];
      const plainOne: Mat = [
        [a + 1, b],
        [b, a + 2],
      ];
      const plainTwo: Mat = [
        [1, k],
        [k + 1, 1],
      ];

      return choiceExercise({
        prompt: 'Which of these matrices has no inverse?',
        ...options(
          [
            { latex: pmatrix(singular), correct: true },
            { latex: pmatrix(addsToZero), misconceptionId: 'determinant-products-added' },
            { latex: pmatrix(plainOne) },
            { latex: pmatrix(plainTwo) },
          ],
          rng.shuffle,
        ),
        summary: `A 2×2 matrix fails to have an inverse exactly when ad − bc = 0. Here the second row is ${k} times the first, so the two rows carry the same information and the determinant vanishes.`,
        hint: 'Compute ad − bc for each. One of them is zero; another is zero only if you add the two products instead of subtracting them.',
      });
    },
  }),

  /* ---------------------------------------------------------- determinants */
  template({
    id: 'determinant-2x2',
    conceptId: 'determinants',
    description: 'The determinant of a 2×2 matrix.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = nonZero(rng, 6);
      const b = nonZero(rng, 6);
      const c = nonZero(rng, 6);
      const d = nonZero(rng, 6);
      const matrix: Mat = [
        [a, b],
        [c, d],
      ];
      const answer = a * d - b * c;

      return numericExercise({
        prompt: 'Find the determinant.',
        latex: pmatrix(matrix),
        value: answer,
        summary: `ad − bc = (${minus(a)})(${minus(d)}) − (${minus(b)})(${minus(c)}) = ${minus(a * d)} − ${minus(b * c)} = ${minus(answer)}.`,
        hint: 'Down-right product minus down-left product. The minus sign is the whole content of the formula.',
        traps: traps(answer, [
          [a * d + b * c, 'determinant-products-added'],
          [b * c - a * d, 'determinant-products-added'],
        ]),
      });
    },
  }),

  template({
    id: 'determinant-3x3',
    conceptId: 'determinants',
    description: 'A 3×3 determinant by expansion along the top row.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const matrix: Mat = [vector(rng, 3, 3), vector(rng, 3, 3), vector(rng, 3, 3)];
      const answer = det3(matrix);
      const allAdded =
        entryOf(matrix, 0, 0) * (entryOf(matrix, 1, 1) * entryOf(matrix, 2, 2) - entryOf(matrix, 1, 2) * entryOf(matrix, 2, 1)) +
        entryOf(matrix, 0, 1) * (entryOf(matrix, 1, 0) * entryOf(matrix, 2, 2) - entryOf(matrix, 1, 2) * entryOf(matrix, 2, 0)) +
        entryOf(matrix, 0, 2) * (entryOf(matrix, 1, 0) * entryOf(matrix, 2, 1) - entryOf(matrix, 1, 1) * entryOf(matrix, 2, 0));

      return numericExercise({
        prompt: 'Find the determinant.',
        latex: pmatrix(matrix),
        value: answer,
        summary: `Expanding along the top row with the alternating signs + − + gives ${minus(answer)}.`,
        hint: 'Three 2×2 determinants, weighted by the top row, with the middle one subtracted.',
        steps: [
          {
            label: 'First minor.',
            latex: `${entryOf(matrix, 0, 0)}\\begin{vmatrix} ${entryOf(matrix, 1, 1)} & ${entryOf(matrix, 1, 2)} \\\\ ${entryOf(matrix, 2, 1)} & ${entryOf(matrix, 2, 2)} \\end{vmatrix}`,
          },
          {
            label: 'Second minor — subtracted.',
            latex: `-\\,${entryOf(matrix, 0, 1)}\\begin{vmatrix} ${entryOf(matrix, 1, 0)} & ${entryOf(matrix, 1, 2)} \\\\ ${entryOf(matrix, 2, 0)} & ${entryOf(matrix, 2, 2)} \\end{vmatrix}`,
            note: 'The alternating sign belongs to the position, not to the number sitting there.',
          },
          {
            label: 'Third minor.',
            latex: `+\\,${entryOf(matrix, 0, 2)}\\begin{vmatrix} ${entryOf(matrix, 1, 0)} & ${entryOf(matrix, 1, 1)} \\\\ ${entryOf(matrix, 2, 0)} & ${entryOf(matrix, 2, 1)} \\end{vmatrix}`,
          },
          { label: 'Total.', latex: `\\det A = ${answer}` },
        ],
        traps: traps(answer, [[allAdded, 'determinant-products-added']]),
      });
    },
  }),

  template({
    id: 'determinant-scaling-choice',
    conceptId: 'determinants',
    description: 'What scaling a whole matrix does to its determinant.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const n = rng.int(2, 4);
      const k = rng.int(2, 4);
      const base = nonZero(rng, 9);
      const answer = k ** n * base;

      return choiceExercise({
        prompt: `A is an ${n}×${n} matrix with determinant ${minus(base)}. What is the determinant of ${k}A?`,
        ...options(
          [
            { latex: `${answer}`, correct: true },
            { latex: `${k * base}`, misconceptionId: 'determinant-scalar-factored-once' },
            // k^(n+1) rather than k^2: at n = 2 a squared factor is the answer.
            { latex: `${k ** (n + 1) * base}` },
            { latex: `${base}` },
          ],
          rng.shuffle,
        ),
        summary: `Multiplying A by ${k} multiplies every one of its ${n} rows by ${k}, and a determinant is linear in each row separately. So the factor comes out ${n} times: det(${k}A) = ${k}^{${n}} · (${minus(base)}) = ${minus(answer)}.`,
        hint: 'Ask how many rows the scalar touches. A determinant is linear in each row on its own, not in the matrix as a whole.',
      });
    },
  }),

  template({
    id: 'determinant-of-sum',
    conceptId: 'determinants',
    description: 'Add the matrices first — det does not distribute over a sum.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a: Mat = [vector(rng, 2, 4), vector(rng, 2, 4)];
      const b: Mat = [vector(rng, 2, 4), vector(rng, 2, 4)];
      const sum = a.map((row, i) => row.map((value, j) => value + entryOf(b, i, j)));
      const answer = det2(sum);

      return numericExercise({
        prompt: 'Find det(A + B).',
        latex: `A = ${pmatrix(a)}, \\quad B = ${pmatrix(b)}`,
        value: answer,
        summary: `Add the matrices first — A + B = ${pmatrix(sum)} — and then take one determinant: ${minus(answer)}.`,
        hint: 'There is no shortcut here. Determinants multiply across a product; they do nothing useful across a sum.',
        traps: traps(answer, [[det2(a) + det2(b), 'determinant-of-sum-split']]),
      });
    },
  }),

  /* --------------------------------------------------------- linear systems */
  template({
    id: 'coefficient-matrix-entry',
    conceptId: 'linear-systems',
    description: 'Read an entry of A off a system written in equations.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a: Mat = [];
      for (let i = 0; i < 3; i += 1) {
        const row: number[] = [];
        for (let j = 0; j < 3; j += 1) row.push(nonZero(rng, 5));
        a.push(row);
      }
      const i = rng.int(0, 2);
      let j = rng.int(0, 2);
      if (j === i) j = (j + 1) % 3;
      if (entryOf(a, i, j) === entryOf(a, j, i)) a[i] = rowOf(a, i).map((value, index) => (index === j ? value + 1 : value));

      const b = [nonZero(rng, 6), nonZero(rng, 6), nonZero(rng, 6)];
      const equations = a
        .map((row, index) =>
          expr(
            leadingTerm(at(row, 0), 'x'),
            signedTerm(at(row, 1), 'y'),
            signedTerm(at(row, 2), 'z'),
            '&=',
            String(at(b, index)),
          ),
        )
        .join(' \\\\ ');
      const answer = entryOf(a, i, j);

      return numericExercise({
        prompt: `Write this system as Ax = b. What is the entry in row ${i + 1}, column ${j + 1} of A?`,
        latex: `\\begin{aligned} ${equations} \\end{aligned}`,
        value: answer,
        summary: `One equation is one row. Row ${i + 1} is equation ${i + 1}, and column ${j + 1} holds the coefficient of ${['x', 'y', 'z'][j]}, which is ${minus(answer)}.`,
        hint: 'Rows are equations; columns are unknowns. Find equation ' + (i + 1) + ' first, then look for the right letter in it.',
        traps: traps(answer, [[entryOf(a, j, i), 'system-coefficients-read-by-column']]),
      });
    },
  }),

  template({
    id: 'system-matrix-form-choice',
    conceptId: 'linear-systems',
    description: 'Pick the matrix equation that says the same thing as the system.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a: Mat = [
        [nonZero(rng, 5), nonZero(rng, 5)],
        [nonZero(rng, 5), nonZero(rng, 5)],
      ];
      if (entryOf(a, 0, 1) === entryOf(a, 1, 0)) a[0] = [entryOf(a, 0, 0), entryOf(a, 0, 1) + 1];
      const b = [nonZero(rng, 6), nonZero(rng, 6)];
      const transposed: Mat = [
        [entryOf(a, 0, 0), entryOf(a, 1, 0)],
        [entryOf(a, 0, 1), entryOf(a, 1, 1)],
      ];
      const equations = a
        .map((row, index) => expr(leadingTerm(at(row, 0), 'x'), signedTerm(at(row, 1), 'y'), '&=', String(at(b, index))))
        .join(' \\\\ ');
      const xy = '\\begin{pmatrix} x \\\\ y \\end{pmatrix}';

      return choiceExercise({
        prompt: 'Which matrix equation says the same thing as this system?',
        latex: `\\begin{aligned} ${equations} \\end{aligned}`,
        ...options(
          [
            { latex: `${pmatrix(a)}${xy} = ${colVec(b)}`, correct: true },
            { latex: `${pmatrix(transposed)}${xy} = ${colVec(b)}`, misconceptionId: 'system-coefficients-read-by-column' },
            { latex: `${pmatrix(a)}${colVec(b)} = ${xy}` },
            { latex: `${xy}${pmatrix(a)} = ${colVec(b)}` },
          ],
          rng.shuffle,
        ),
        summary:
          'Each equation becomes one row of A, in the order the unknowns appear, and the constants become b. Reading the equations into columns instead transposes A, which is a different system.',
        hint: 'Check by multiplying out the top row of your choice — it should reproduce the first equation exactly.',
      });
    },
  }),

  template({
    id: 'column-view-choice',
    conceptId: 'linear-systems',
    description: 'Say what the entries of x multiply in Ax = b.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a: Mat = [vector(rng, 3, 4), vector(rng, 3, 4), vector(rng, 3, 4)];
      const b = vector(rng, 3, 6);

      return choiceExercise({
        prompt: 'In the equation Ax = b, what does the second entry of x multiply?',
        latex: `A = ${pmatrix(a)}, \\quad \\mathbf{b} = ${colVec(b)}`,
        ...options(
          [
            { label: 'The second column of A', correct: true },
            { label: 'The second row of A', misconceptionId: 'matrix-equation-rows-and-columns-confused' },
            { label: 'The second entry of b' },
            { label: 'The whole of A' },
          ],
          rng.shuffle,
        ),
        summary:
          'Ax is a weighted sum of the columns of A, with the entries of x as the weights. That is the reading that turns "is this system solvable?" into "is b reachable from these columns?" — the rows give the other reading, one equation each.',
        hint: 'Write Ax out as x₁ times something plus x₂ times something. What are those somethings?',
      });
    },
  }),

  /* ------------------------------------------------------------------ rank */
  template({
    id: 'rank-of-matrix',
    conceptId: 'rank',
    description: 'The rank of a small matrix built to have a known one.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const height = rng.int(3, 4);
      const width = rng.int(3, 4);
      const rank = rng.int(1, Math.min(height, width) - 1);
      const matrix = matrixWithRank(rng, height, width, rank);

      return numericExercise({
        prompt: 'What is the rank of this matrix?',
        latex: pmatrix(matrix),
        value: rank,
        summary: `Row reducing leaves ${rank} pivot${rank === 1 ? '' : 's'}. The remaining rows were combinations of those, so they carried no information the first ${rank} did not already carry.`,
        hint: 'Rank is the number of pivots after reduction — equivalently, how many of these rows say something genuinely new.',
        traps: traps(rank, [
          [height, 'rank-taken-as-matrix-size'],
          [Math.min(height, width), 'rank-taken-as-matrix-size'],
        ]),
      });
    },
  }),

  template({
    id: 'rank-nullity-nullity',
    conceptId: 'rank',
    description: 'Get the nullity from the rank and the number of columns.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const rows = rng.int(3, 6);
      let columns = rng.int(3, 7);
      if (columns === rows) columns = rows + 1;
      const rank = rng.int(1, Math.min(rows, columns) - 1);
      const answer = columns - rank;

      return numericExercise({
        prompt: `A is a ${rows}×${columns} matrix of rank ${rank}. What is the dimension of its null space?`,
        value: answer,
        summary: `rank + nullity = number of columns, so the nullity is ${columns} − ${rank} = ${answer}. The columns are the variables, and each is either a pivot or free.`,
        hint: 'The theorem is about columns. Rows never appear in it, however tempting the other subtraction looks.',
        steps: [
          { label: 'State the theorem.', latex: `\\operatorname{rank}(A) + \\operatorname{nullity}(A) = n` },
          { label: 'Here n is the number of columns.', latex: `n = ${columns}` },
          { label: 'Rearrange.', latex: `\\operatorname{nullity}(A) = ${columns} - ${rank} = ${answer}` },
        ],
        traps: traps(answer, [
          [rows - rank, 'rank-nullity-rows-used'],
          [rank, 'null-space-confused-with-column-space'],
        ]),
      });
    },
  }),

  template({
    id: 'largest-possible-rank',
    conceptId: 'rank',
    description: 'The ceiling on the rank of a rectangular matrix.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      let rows = rng.int(2, 7);
      let columns = rng.int(2, 7);
      if (rows === columns) columns = rows + 1;
      const answer = Math.min(rows, columns);

      return numericExercise({
        prompt: `What is the largest rank a ${rows}×${columns} matrix can have?`,
        value: answer,
        summary: `Every pivot needs a row of its own and a column of its own, so the rank cannot exceed either count: the ceiling is min(${rows}, ${columns}) = ${answer}.`,
        hint: 'A pivot occupies one row and one column exclusively. Whichever of the two runs out first sets the limit.',
        traps: traps(answer, [
          [Math.max(rows, columns), 'rank-taken-as-matrix-size'],
          [rows * columns, 'rank-taken-as-matrix-size'],
        ]),
      });
    },
  }),

  template({
    id: 'rank-consequence-choice',
    conceptId: 'rank',
    description: 'What a deficient rank tells you about a square matrix.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const n = rng.int(3, 5);
      const rank = rng.int(1, n - 1);

      return choiceExercise({
        prompt: `A is an ${n}×${n} matrix of rank ${rank}. Which statement is true?`,
        ...options(
          [
            { label: 'Its determinant is 0', correct: true },
            { label: 'It is invertible', misconceptionId: 'rank-taken-as-matrix-size' },
            { label: 'Ax = 0 has only the zero solution', misconceptionId: 'rank-nullity-rows-used' },
            { label: `It has exactly ${rank} rows` },
          ],
          rng.shuffle,
        ),
        summary: `Rank ${rank} in an ${n}×${n} matrix means ${n - rank} column${n - rank === 1 ? '' : 's'} without a pivot, so Ax = 0 has non-zero solutions and A cannot be inverted. A square matrix is invertible exactly when its rank equals its size.`,
        hint: 'Compare the rank with the size. Full rank and invertible are the same statement for a square matrix.',
      });
    },
  }),

  /* ----------------------------------------------------------- solution sets */
  template({
    id: 'solution-count-from-ranks',
    conceptId: 'solution-sets',
    description: 'Classify a system from the two ranks and the unknown count.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const unknowns = rng.int(3, 5);
      const kind = rng.pick(['none', 'one', 'many'] as const);
      const rank = kind === 'one' ? unknowns : rng.int(1, unknowns - 1);
      const augmented = kind === 'none' ? rank + 1 : rank;
      const isNone = kind === 'none';

      return choiceExercise({
        prompt: `A system in ${unknowns} unknowns has rank(A) = ${rank} and rank of the augmented matrix = ${augmented}. How many solutions does it have?`,
        ...options(
          [
            { label: 'No solutions', correct: isNone },
            {
              label: 'Exactly one solution',
              correct: kind === 'one',
              ...(isNone ? { misconceptionId: 'inconsistent-row-read-as-solvable' } : {}),
            },
            {
              label: 'Infinitely many solutions',
              correct: kind === 'many',
              ...(isNone ? { misconceptionId: 'underdetermined-assumed-consistent' } : {}),
            },
            { label: 'Exactly two solutions' },
          ],
          rng.shuffle,
        ),
        summary: isNone
          ? `The augmented rank is larger than rank(A), so the reduction produced a row reading 0 = c with c non-zero. That is a contradiction, and the system has no solutions — the ${unknowns - rank} free variables it would otherwise have make no difference.`
          : kind === 'one'
            ? `The two ranks agree, so the system is consistent, and rank ${rank} equals the ${unknowns} unknowns, so every variable has a pivot. That pins each one to a single value.`
            : `The two ranks agree, so the system is consistent, and ${unknowns} − ${rank} = ${unknowns - rank} variable${unknowns - rank === 1 ? ' is' : 's are'} free. Every choice of those gives another solution.`,
        hint: 'Two questions in order: do the ranks agree (is it consistent?), and does the rank reach the number of unknowns (is it unique?).',
      });
    },
  }),

  template({
    id: 'parametric-solution-component',
    conceptId: 'solution-sets',
    description: 'Evaluate a parametrised solution set at a given parameter.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const particular = vector(rng, 3, 5);
      const direction = vector(rng, 3, 3);
      const t = nonZero(rng, 4, [1]);
      const i = rng.int(0, 2);
      if (at(direction, i) === 0) direction[i] = nonZero(rng, 3);
      if (at(particular, i) === 0) particular[i] = nonZero(rng, 5);
      const answer = at(particular, i) + t * at(direction, i);

      return numericExercise({
        prompt: `The solutions of Ax = b are x = p + t·h. Find the ${ordinal(i)} component of the solution with t = ${minus(t)}.`,
        latex: named([
          ['p', particular],
          ['h', direction],
        ]),
        value: answer,
        summary: `Substituting t = ${minus(t)} gives ${minus(at(particular, i))} + (${minus(t)})(${minus(at(direction, i))}) = ${minus(answer)} in the ${ordinal(i)} slot.`,
        hint: 'The particular solution does not scale with t — only the direction vector does.',
        traps: traps(answer, [
          [t * at(direction, i), 'solution-set-taken-as-subspace'],
          [at(particular, i) + at(direction, i), 'combination-scalars-ignored'],
        ]),
      });
    },
  }),

  template({
    id: 'solution-set-dimension',
    conceptId: 'solution-sets',
    description: 'The dimension of the flat that a consistent system solves to.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const rows = rng.int(3, 6);
      let columns = rng.int(3, 6);
      if (columns === rows) columns = rows + 1;
      const rank = rng.int(1, Math.min(rows, columns) - 1);
      const answer = columns - rank;

      return numericExercise({
        prompt: `A consistent system has ${columns} unknowns and its ${rows}×${columns} coefficient matrix has rank ${rank}. The solution set is a translate of a subspace — of what dimension?`,
        value: answer,
        summary: `The solutions are one particular solution plus everything solving Ax = 0, and that null space has dimension ${columns} − ${rank} = ${answer}. Translating does not change a dimension.`,
        hint: 'The shape of the solution set is the shape of the null space, moved off the origin by one particular solution.',
        traps: traps(answer, [
          [rows - rank, 'rank-nullity-rows-used'],
          [rank, 'null-space-confused-with-column-space'],
        ]),
      });
    },
  }),

  /* ---------------------------------------------------------- vector spaces */
  template({
    id: 'vector-space-choice',
    conceptId: 'vector-spaces',
    description: 'Pick the set that really is a vector space.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const n = rng.int(2, 5);
      const good = rng.pick([
        `All ${n}×${n} matrices`,
        `All polynomials of degree at most ${n}`,
        'All functions from ℝ to ℝ',
        `All vectors in ℝ^${n}`,
      ]);
      const bad = rng.shuffle([
        { label: `All polynomials of degree exactly ${n}`, misconceptionId: 'vector-space-zero-vector-missing' },
        { label: `All ${n}×${n} matrices with determinant 0`, misconceptionId: 'closure-checked-only-for-addition' },
        { label: `All vectors in ℝ^${n} with whole-number entries`, misconceptionId: 'closure-checked-only-for-addition' },
        { label: `All vectors in ℝ^${n} whose entries add up to 1`, misconceptionId: 'vector-space-zero-vector-missing' },
        { label: `All invertible ${n}×${n} matrices`, misconceptionId: 'vector-space-zero-vector-missing' },
      ]).slice(0, 3);

      return choiceExercise({
        prompt: 'Which of these sets is a vector space, with the usual addition and scaling?',
        ...options([{ label: good, correct: true }, ...bad], rng.shuffle),
        summary:
          'Two questions settle almost every case: does the set contain a zero, and does it stay inside itself when you add two members or scale one? A set defined by an equation with a non-zero right-hand side fails the first; a set defined by an inequality or by a determinant condition usually fails the second.',
        hint: 'Test the zero first — it is one line, and it eliminates most of the impostors immediately.',
      });
    },
  }),

  template({
    id: 'zero-vector-choice',
    conceptId: 'vector-spaces',
    description: 'Name the zero vector of an unfamiliar space.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const n = rng.int(2, 4);
      const kind = rng.pick(['polynomials', 'matrices', 'functions'] as const);

      const CASES = {
        polynomials: {
          space: `the polynomials of degree at most ${n}`,
          correct: 'The polynomial that is 0 everywhere',
          others: ['The polynomial x', `The polynomial x^${n}`, 'The number 1'],
        },
        matrices: {
          space: `the ${n}×${n} matrices`,
          correct: `The ${n}×${n} matrix of all zeros`,
          others: [`The ${n}×${n} identity matrix`, 'The number 0', 'There is no zero vector'],
        },
        functions: {
          space: 'the functions from ℝ to ℝ',
          correct: 'The function f with f(x) = 0 for every x',
          others: ['The function f with f(x) = x', 'The empty function', 'There is no zero vector'],
        },
      } as const;

      const chosen = CASES[kind];

      return choiceExercise({
        prompt: `What plays the role of the zero vector in ${chosen.space}?`,
        ...options(
          [
            { label: chosen.correct, correct: true },
            { label: chosen.others[0] ?? '' },
            { label: chosen.others[1] ?? '' },
            { label: chosen.others[2] ?? '', misconceptionId: 'vector-space-zero-vector-missing' },
          ],
          rng.shuffle,
        ),
        summary:
          'The zero vector is whatever leaves every member unchanged when you add it. That is a role, not a symbol — in one space it is a list of zeros, in another a matrix of zeros, in another the function that is flat at zero.',
        hint: 'Ask what you can add to any member of this set and get that member back.',
      });
    },
  }),

  template({
    id: 'failing-axiom-choice',
    conceptId: 'vector-spaces',
    description: 'Name the axiom a near-miss set actually breaks.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const c = nonZero(rng, 6, [0]);
      const CASES = [
        {
          set: 'the vectors in ℝ² with x ≥ 0',
          axiom: 'Closure under scalar multiplication',
          why: 'Multiplying by −1 sends a member out of the set, and −1 is a perfectly legal scalar. Addition, on the other hand, never leaves it.',
        },
        {
          set: `the vectors in ℝ³ with x + y + z = ${c}`,
          axiom: 'The existence of a zero vector',
          why: `The zero vector has x + y + z = 0, and ${minus(c)} is not 0, so the set does not contain it at all. Every other axiom is then beside the point.`,
        },
        {
          set: 'the vectors in ℝ² with whole-number entries',
          axiom: 'Closure under scalar multiplication',
          why: 'Adding two of them stays inside, so addition is fine. Halving one does not, and a half is an ordinary real scalar.',
        },
      ] as const;
      const chosen = rng.pick(CASES);

      /*
       * The four axioms are always offered, whichever one actually fails, so
       * the correct answer cannot be found by noticing which option is missing.
       * A wrong choice earns a diagnosis only where it names a real habit:
       * blaming addition when scaling is what breaks, and blaming the zero
       * vector when the set does contain one.
       */
      const AXIOMS = [
        'Closure under addition',
        'Closure under scalar multiplication',
        'The existence of a zero vector',
        'Commutativity of addition',
      ] as const;
      const WHEN_WRONG: Record<string, string | undefined> = {
        'Closure under addition': 'closure-checked-only-for-addition',
        'The existence of a zero vector': 'vector-space-zero-vector-missing',
      };

      return choiceExercise({
        prompt: `Which vector space axiom fails for ${chosen.set}?`,
        ...options(
          AXIOMS.map((axiom) => {
            const misconceptionId = axiom === chosen.axiom ? undefined : WHEN_WRONG[axiom];
            return {
              label: axiom,
              correct: axiom === chosen.axiom,
              ...(misconceptionId ? { misconceptionId } : {}),
            };
          }),
          rng.shuffle,
        ),
        summary: chosen.why,
        hint: 'Try the zero first, then addition, then a scalar — and remember that negative and fractional scalars are allowed.',
      });
    },
  }),

  /* -------------------------------------------------------------- subspaces */
  template({
    id: 'subspace-choice',
    conceptId: 'subspaces',
    description: 'Pick the subset of ℝ³ that is a subspace.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = nonZero(rng, 4);
      const b = nonZero(rng, 4);
      const c = nonZero(rng, 4);
      const d = nonZero(rng, 6);
      const plane = `${expr(leadingTerm(a, 'x'), signedTerm(b, 'y'), signedTerm(c, 'z'))} = 0`;

      return choiceExercise({
        prompt: 'Which of these subsets of ℝ³ is a subspace?',
        ...options(
          [
            { label: `The plane ${plane}`, correct: true },
            { label: `The plane ${expr(leadingTerm(a, 'x'), signedTerm(b, 'y'), signedTerm(c, 'z'))} = ${d}`, misconceptionId: 'subspace-not-through-origin' },
            { label: 'The vectors with x ≥ 0', misconceptionId: 'closure-checked-only-for-addition' },
            { label: 'The vectors with xyz = 0', misconceptionId: 'closure-checked-only-for-addition' },
          ],
          rng.shuffle,
        ),
        summary: `A subspace has to contain the zero vector and stay inside itself under addition and scaling. The plane ${plane} passes through the origin and is closed under both; a plane with a non-zero right-hand side fails at the origin, and both of the other two fail closure.`,
        hint: 'Substitute the zero vector into each description. Whatever survives that, test with a negative scalar.',
      });
    },
  }),

  template({
    id: 'null-space-dimension',
    conceptId: 'subspaces',
    description: 'The dimension of the null space of a given matrix.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const height = 3;
      const width = rng.int(3, 4);
      const rank = rng.int(1, 2);
      const matrix = matrixWithRank(rng, height, width, rank);
      const answer = width - rank;

      return numericExercise({
        prompt: 'What is the dimension of the null space of this matrix?',
        latex: pmatrix(matrix),
        value: answer,
        summary: `The matrix reduces to ${rank} pivot${rank === 1 ? '' : 's'}, so of the ${width} columns, ${answer} have none. Each pivot-free column is a free variable, and each free variable contributes one dimension to the null space.`,
        hint: 'Count the pivots, then subtract from the number of columns — the columns are the variables.',
        traps: traps(answer, [
          [rank, 'null-space-confused-with-column-space'],
          [height - rank, 'rank-nullity-rows-used'],
        ]),
      });
    },
  }),

  template({
    id: 'column-space-dimension',
    conceptId: 'subspaces',
    description: 'The dimension of the column space of a given matrix.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const height = rng.int(3, 4);
      const width = 4;
      const rank = rng.int(1, 3);
      const matrix = matrixWithRank(rng, height, width, Math.min(rank, height));
      const actual = rankOf(matrix);

      return numericExercise({
        prompt: 'What is the dimension of the column space of this matrix?',
        latex: pmatrix(matrix),
        value: actual,
        summary: `The column space is spanned by the ${width} columns, but only ${actual} of them are independent — the pivot columns. Its dimension is therefore ${actual}.`,
        hint: 'The column space lives among the outputs. Its dimension is the rank, which is the pivot count.',
        traps: traps(actual, [
          [width - actual, 'null-space-confused-with-column-space'],
          [width, 'dimension-counted-as-vector-length'],
        ]),
      });
    },
  }),

  /* ------------------------------------------------------------------ basis */
  template({
    id: 'basis-recognition-choice',
    conceptId: 'basis',
    description: 'Pick the set that is a basis for ℝ³.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const good = matrixWithRank(rng, 3, 3, 3);
      const flat = matrixWithRank(rng, 3, 3, 2);
      const setOf = (columns: readonly (readonly number[])[]): string =>
        `\\left\\{ ${columns.map((column) => colVec(column)).join(', ')} \\right\\}`;
      const goodColumns = [columnOf(good, 0), columnOf(good, 1), columnOf(good, 2)];
      const flatColumns = [columnOf(flat, 0), columnOf(flat, 1), columnOf(flat, 2)];
      const extra = vector(rng, 3, 3);

      return choiceExercise({
        prompt: 'Which of these sets is a basis for ℝ³?',
        ...options(
          [
            { latex: setOf(goodColumns), correct: true },
            { latex: setOf(flatColumns), misconceptionId: 'independence-checked-pairwise' },
            { latex: setOf(goodColumns.slice(0, 2)), misconceptionId: 'basis-independent-without-spanning' },
            { latex: setOf([...goodColumns, extra]), misconceptionId: 'basis-spanning-without-independence' },
          ],
          rng.shuffle,
        ),
        summary:
          'A basis has to do two jobs: reach everything, and waste nothing. Two vectors cannot reach all of ℝ³; four vectors in ℝ³ always contain a redundant one; and three vectors only work if none of them lies in the plane of the other two.',
        hint: 'For ℝ³ the count has to be exactly three — and then you still have to check that none of them was already reachable from the others.',
      });
    },
  }),

  template({
    id: 'basis-size-of-span',
    conceptId: 'basis',
    description: 'How many vectors a basis for a given span contains.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const count = 4;
      const ambient = 5;
      const rank = rng.int(1, 3);
      const matrix = matrixWithRank(rng, count, ambient, rank);
      const vectors = matrix.map((row) => [...row]);

      return numericExercise({
        prompt: `These four vectors live in ℝ^${ambient}. How many vectors does a basis for their span contain?`,
        latex: named([
          ['a', vectors[0] ?? []],
          ['b', vectors[1] ?? []],
          ['c', vectors[2] ?? []],
          ['d', vectors[3] ?? []],
        ]),
        value: rank,
        summary: `Only ${rank} of the four are independent; the rest are combinations of those. A basis keeps exactly the independent ones, so it has ${rank} vector${rank === 1 ? '' : 's'}.`,
        hint: 'A basis for a span is the largest independent subset of it. Redundant vectors are dropped, not counted.',
        traps: traps(rank, [
          [count, 'basis-spanning-without-independence'],
          [ambient, 'dimension-counted-as-vector-length'],
        ]),
      });
    },
  }),

  template({
    id: 'basis-completion-count',
    conceptId: 'basis',
    description: 'How many vectors are still missing from a basis.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const ambient = rng.int(4, 7);
      const have = rng.int(1, ambient - 1);
      const answer = ambient - have;

      return numericExercise({
        prompt: `You have ${have} linearly independent vectors in ℝ^${ambient}. How many more do you need before they form a basis?`,
        value: answer,
        summary: `A basis for ℝ^${ambient} has exactly ${ambient} vectors, and the ${have} you have are independent and can all be kept. So ${ambient} − ${have} = ${answer} more are needed.`,
        hint: 'Every basis of the same space has the same size, and for ℝⁿ that size is n.',
        traps: traps(answer, [
          [ambient, 'dimension-counted-as-vector-length'],
          [have, 'basis-independent-without-spanning'],
        ]),
      });
    },
  }),

  /* -------------------------------------------------------------- dimension */
  template({
    id: 'dimension-of-standard-space',
    conceptId: 'dimension',
    description: 'The dimension of a familiar space, counted from its basis.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const polynomial = rng.bool();
      const n = rng.int(2, 5);
      const p = rng.int(2, 4);
      const q = rng.int(2, 4);
      const answer = polynomial ? n + 1 : p * q;

      return numericExercise({
        prompt: polynomial
          ? `What is the dimension of the space of polynomials of degree at most ${n}?`
          : `What is the dimension of the space of ${p}×${q} matrices?`,
        value: answer,
        summary: polynomial
          ? `A basis is 1, x, x², …, x^${n} — that is ${n} + 1 = ${answer} polynomials, because the constant one counts.`
          : `A basis is the ${p * q} matrices with a single 1 and zeros elsewhere, so the dimension is ${p} × ${q} = ${answer}.`,
        hint: 'Write down a basis and count it. The dimension is never guessed from a name.',
        traps: polynomial
          ? traps(answer, [[n, 'polynomial-degree-taken-as-dimension']])
          : traps(answer, [[p + q, 'dimension-counted-as-vector-length']]),
      });
    },
  }),

  template({
    id: 'rank-from-nullity',
    conceptId: 'dimension',
    description: 'Recover the rank of a transformation from its nullity.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const n = rng.int(4, 7);
      let m = rng.int(3, 6);
      if (m === n) m = n + 1;
      const nullity = rng.int(1, n - 1);
      const answer = n - nullity;

      return numericExercise({
        prompt: `T maps ℝ^${n} to ℝ^${m} and its kernel has dimension ${nullity}. What is the dimension of its image?`,
        value: answer,
        summary: `Rank–nullity is about the *domain*: rank + nullity = ${n}, so the image has dimension ${n} − ${nullity} = ${answer}.`,
        hint: 'The total that gets split is the dimension of the space you start in, not the one you land in.',
        steps: [
          { label: 'The theorem.', latex: `\\dim(\\ker T) + \\dim(\\operatorname{im} T) = \\dim(\\text{domain})` },
          { label: 'The domain here.', latex: `\\dim(\\text{domain}) = ${n}` },
          { label: 'Rearrange.', latex: `\\dim(\\operatorname{im} T) = ${n} - ${nullity} = ${answer}` },
        ],
        traps: traps(answer, [
          [m - nullity, 'rank-nullity-rows-used'],
          [nullity, 'kernel-confused-with-range'],
        ]),
      });
    },
  }),

  template({
    id: 'dimension-consequence-choice',
    conceptId: 'dimension',
    description: 'What knowing the dimension guarantees about sets of vectors.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const d = rng.int(3, 7);

      return choiceExercise({
        prompt: `V is a vector space of dimension ${d}. Which statement is guaranteed?`,
        ...options(
          [
            { label: `Any ${d + 1} vectors in V are linearly dependent`, correct: true },
            { label: `Any ${d} vectors in V form a basis`, misconceptionId: 'basis-spanning-without-independence' },
            { label: `Any ${d - 1} vectors in V span V`, misconceptionId: 'basis-independent-without-spanning' },
            { label: `V contains exactly ${d} vectors`, misconceptionId: 'dimension-counted-as-vector-length' },
          ],
          rng.shuffle,
        ),
        summary: `Dimension ${d} means every basis has ${d} vectors. More than ${d} vectors must therefore be dependent, and fewer than ${d} cannot span — but exactly ${d} vectors are a basis only when they are also independent.`,
        hint: 'Dimension bounds two things at once: how many vectors can be independent, and how few can span.',
      });
    },
  }),

  /* ------------------------------------------------------------ coordinates */
  template({
    id: 'coordinate-in-basis',
    conceptId: 'coordinates',
    description: 'Find a coordinate of a vector in a non-standard basis.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const b1 = [rng.int(1, 3), rng.int(-3, 3)];
      let b2 = [rng.int(-3, 3), rng.int(1, 3)];
      if (det2([b1, b2]) === 0) b2 = [at(b2, 0) + 1, at(b2, 1) + 2];
      const c1 = nonZero(rng, 4);
      const c2 = nonZero(rng, 4, [c1]);
      const v = [c1 * at(b1, 0) + c2 * at(b2, 0), c1 * at(b1, 1) + c2 * at(b2, 1)];
      if (at(v, 0) === c1) v[0] = at(v, 0); // The trap survives on its own merits below.

      return numericExercise({
        prompt: 'B is the basis {b₁, b₂}. Find the first coordinate of v with respect to B.',
        latex: `${named([
          ['b_{1}', b1],
          ['b_{2}', b2],
        ])}, \\quad \\mathbf{v} = ${colVec(v)}`,
        value: c1,
        summary: `Solving c₁b₁ + c₂b₂ = v gives c₁ = ${minus(c1)} and c₂ = ${minus(c2)}, so [v]_B = ${colVec([c1, c2])}. The entries of v itself are its coordinates in the standard basis, not in B.`,
        hint: 'Coordinates in B are the weights that rebuild v out of b₁ and b₂ — so this is a two-by-two system, not a reading-off.',
        traps: traps(c1, [
          [at(v, 0), 'coordinates-taken-as-components'],
          [c2, 'combination-coefficients-swapped'],
        ]),
      });
    },
  }),

  template({
    id: 'standard-from-coordinates',
    conceptId: 'coordinates',
    description: 'Translate coordinates in a basis back into standard entries.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const b1 = vector(rng, 2, 4);
      let b2 = vector(rng, 2, 4);
      if (det2([b1, b2]) === 0) b2 = [at(b1, 1) + 1, at(b1, 0) - 1];
      const c1 = nonZero(rng, 4);
      const c2 = nonZero(rng, 4);
      const i = rng.int(0, 1);
      const answer = c1 * at(b1, i) + c2 * at(b2, i);

      return numericExercise({
        prompt: `A vector has coordinates (${minus(c1)}, ${minus(c2)}) in the basis B. Find its ${ordinal(i)} standard entry.`,
        latex: named([
          ['b_{1}', b1],
          ['b_{2}', b2],
        ]),
        value: answer,
        summary: `The coordinates are weights: v = ${minus(c1)}b₁ ${c2 < 0 ? '−' : '+'} ${Math.abs(c2)}b₂, whose ${ordinal(i)} entry is ${minus(answer)}.`,
        hint: 'Going from coordinates back to entries is just the combination — no system to solve in this direction.',
        traps: traps(answer, [[i === 0 ? c1 : c2, 'coordinates-taken-as-components']]),
      });
    },
  }),

  template({
    id: 'change-of-basis-direction-choice',
    conceptId: 'coordinates',
    description: 'Decide whether P or its inverse does the translation asked for.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const b1 = vector(rng, 2, 4);
      let b2 = vector(rng, 2, 4);
      if (det2([b1, b2]) === 0) b2 = [at(b1, 1) + 1, at(b1, 0) - 1];
      const p: Mat = [
        [at(b1, 0), at(b2, 0)],
        [at(b1, 1), at(b2, 1)],
      ];

      return choiceExercise({
        prompt: 'P has the basis vectors of B as its columns. Which product turns a coordinate vector in B into ordinary entries?',
        latex: `P = ${pmatrix(p)}`,
        ...options(
          [
            { latex: 'P[\\mathbf{v}]_{B}', correct: true },
            { latex: 'P^{-1}[\\mathbf{v}]_{B}', misconceptionId: 'change-of-basis-matrix-inverted' },
            { latex: 'P^{T}[\\mathbf{v}]_{B}' },
            { latex: '[\\mathbf{v}]_{B}P' },
          ],
          rng.shuffle,
        ),
        summary:
          'The columns of P are the basis vectors, so P applied to a list of weights builds the combination those weights describe — which is the vector in standard entries. The inverse runs the other way, from entries to coordinates.',
        hint: 'Apply P to the coordinate vector (1, 0). Whatever comes out should be the first basis vector.',
      });
    },
  }),

  /* -------------------------------------------------- linear transformations */
  template({
    id: 'linearity-choice',
    conceptId: 'linear-transformations',
    description: 'Pick the map that respects addition and scaling.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = nonZero(rng, 4);
      const b = nonZero(rng, 4);
      const c = nonZero(rng, 5);

      return choiceExercise({
        prompt: 'Which of these maps from ℝ² to ℝ² is a linear transformation?',
        ...options(
          [
            { latex: `T(x, y) = (${expr(leadingTerm(a, 'x'), signedTerm(b, 'y'))},\\ ${expr(leadingTerm(b, 'x'), signedTerm(a, 'y'))})`, correct: true },
            { latex: `T(x, y) = (${expr(leadingTerm(a, 'x'), signedConstant(c))},\\ y)`, misconceptionId: 'linearity-assumed-for-affine' },
            { latex: `T(x, y) = (x^{2},\\ ${leadingTerm(b, 'y')})`, misconceptionId: 'closure-checked-only-for-addition' },
            { latex: `T(x, y) = (\\lvert x \\rvert,\\ ${leadingTerm(a, 'y')})` },
          ],
          rng.shuffle,
        ),
        summary:
          'A linear transformation must satisfy T(u + v) = T(u) + T(v) and T(cu) = cT(u). Only a map whose every output is a weighted sum of the inputs does that — a constant term, a square and an absolute value each break it, and all three break it at the zero vector or at a negative scalar.',
        hint: 'Feed each map the zero vector. Anything that does not return the zero vector is out immediately.',
      });
    },
  }),

  template({
    id: 'linearity-from-images',
    conceptId: 'linear-transformations',
    description: 'Use linearity to reach a new image from two known ones.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const tu = vector(rng, 3, 4);
      const tv = vector(rng, 3, 4);
      const a = rng.int(2, 4);
      const b = nonZero(rng, 4, [-1, 1]);
      const i = rng.int(0, 2);
      if (at(tu, i) === 0) tu[i] = nonZero(rng, 4);
      if (at(tv, i) === 0) tv[i] = nonZero(rng, 4);
      const answer = a * at(tu, i) + b * at(tv, i);

      return numericExercise({
        prompt: `T is linear. Given T(u) and T(v), find the ${ordinal(i)} component of T(${a}u ${b < 0 ? `− ${-b}` : `+ ${b}`}v).`,
        latex: `T(\\mathbf{u}) = ${colVec(tu)}, \\quad T(\\mathbf{v}) = ${colVec(tv)}`,
        value: answer,
        summary: `Linearity moves the weights outside: T(${a}u ${b < 0 ? '−' : '+'} ${Math.abs(b)}v) = ${a}T(u) ${b < 0 ? '−' : '+'} ${Math.abs(b)}T(v), whose ${ordinal(i)} component is ${minus(answer)}. No formula for T is needed, and none was given.`,
        hint: 'You are not being asked to find T. The two images you have already determine every image of a combination of u and v.',
        traps: traps(answer, [
          [at(tu, i) + at(tv, i), 'linearity-not-used-on-combination'],
          [a * at(tu, i) + at(tv, i), 'vector-scaled-only-once'],
        ]),
      });
    },
  }),

  template({
    id: 'transformation-image-component',
    conceptId: 'linear-transformations',
    description: 'Apply a transformation given by a matrix to a vector.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a: Mat = [vector(rng, 3, 4), vector(rng, 3, 4), vector(rng, 3, 4)];
      const v = vector(rng, 3, 4);
      const i = rng.int(0, 2);
      const answer = dot(rowOf(a, i), v);

      return numericExercise({
        prompt: `T(x) = Ax. Find the ${ordinal(i)} component of T(v).`,
        latex: `A = ${pmatrix(a)}, \\quad \\mathbf{v} = ${colVec(v)}`,
        value: answer,
        summary: `Row ${i + 1} of A against v gives ${minus(answer)}. Each row of A produces one component of the output.`,
        hint: 'A transformation given by a matrix is applied by multiplying — one row of A per component of the answer.',
        traps: traps(answer, [[dot(columnOf(a, i), v), 'matrix-equation-rows-and-columns-confused']]),
      });
    },
  }),

  /* ------------------------------------------------------------ kernel/range */
  template({
    id: 'kernel-dimension',
    conceptId: 'kernel-range',
    description: 'The dimension of what a transformation collapses.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const height = rng.int(3, 4);
      const width = rng.int(3, 4);
      const rank = rng.int(1, Math.min(height, width) - 1);
      const matrix = matrixWithRank(rng, height, width, rank);
      const actual = rankOf(matrix);
      const answer = width - actual;

      return numericExercise({
        prompt: `T(x) = Ax maps ℝ^${width} to ℝ^${height}. What is the dimension of the kernel of T?`,
        latex: `A = ${pmatrix(matrix)}`,
        value: answer,
        summary: `A has rank ${actual}, so of the ${width} input directions, ${answer} get sent to zero. The kernel is that ${answer}-dimensional subspace of the domain.`,
        hint: 'The kernel lives in the domain, so the count it comes out of is the number of columns.',
        traps: traps(answer, [
          [actual, 'kernel-confused-with-range'],
          [height - actual, 'rank-nullity-rows-used'],
        ]),
      });
    },
  }),

  template({
    id: 'range-dimension',
    conceptId: 'kernel-range',
    description: 'The dimension of what a transformation reaches.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const height = rng.int(3, 4);
      const width = rng.int(3, 4);
      const rank = rng.int(1, Math.min(height, width) - 1);
      const matrix = matrixWithRank(rng, height, width, rank);
      const actual = rankOf(matrix);

      return numericExercise({
        prompt: `T(x) = Ax maps ℝ^${width} to ℝ^${height}. What is the dimension of the image of T?`,
        latex: `A = ${pmatrix(matrix)}`,
        value: actual,
        summary: `The image is spanned by the columns of A, and only ${actual} of them are independent. So the image is an ${actual}-dimensional subspace of ℝ^${height} — a proper one, since ${actual} < ${height}.`,
        hint: 'The image is the span of the columns. Its dimension is the rank.',
        traps: traps(actual, [
          [width - actual, 'kernel-confused-with-range'],
          [height, 'dimension-counted-as-vector-length'],
        ]),
      });
    },
  }),

  template({
    id: 'kernel-range-statement-choice',
    conceptId: 'kernel-range',
    description: 'Read one-to-one and onto off the rank.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const n = rng.int(4, 6);
      const m = rng.int(3, 5);
      const rank = Math.max(1, Math.min(n, m) - rng.int(1, 2));
      const nullity = n - rank;

      return choiceExercise({
        prompt: `T maps ℝ^${n} to ℝ^${m}, and its matrix has rank ${rank}. Which statement is true?`,
        ...options(
          [
            { label: 'T is not one-to-one', correct: true },
            { label: `T is onto ℝ^${m}` },
            { label: 'The kernel of T is empty', misconceptionId: 'kernel-taken-as-empty' },
            {
              label: 'The kernel and the image have the same dimension',
              ...(nullity === rank ? {} : { misconceptionId: 'kernel-confused-with-range' }),
            },
          ],
          rng.shuffle,
        ),
        summary: `Rank ${rank} with ${n} columns leaves a kernel of dimension ${nullity}, so distinct inputs collide and T is not one-to-one. It is not onto either: the image has dimension ${rank}, short of the ${m} that ℝ^${m} would need. And the kernel is never empty — it always contains the zero vector.`,
        hint: 'Compare the rank with the number of columns for one-to-one, and with the number of rows for onto.',
      });
    },
  }),

  /* --------------------------------------------------- the matrix of a map */
  template({
    id: 'standard-matrix-entry',
    conceptId: 'transformation-matrix',
    description: 'Build the standard matrix from the images of the basis vectors.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const te1 = vector(rng, 2, 5);
      const te2 = vector(rng, 2, 5);
      if (at(te1, 1) === at(te2, 0)) te2[0] = at(te2, 0) + 1;
      const matrix: Mat = [
        [at(te1, 0), at(te2, 0)],
        [at(te1, 1), at(te2, 1)],
      ];
      const i = rng.int(0, 1);
      const j = i === 0 ? 1 : 0;
      const answer = entryOf(matrix, i, j);

      return numericExercise({
        prompt: `T is linear. Find the entry in row ${i + 1}, column ${j + 1} of its standard matrix.`,
        latex: `T(\\mathbf{e}_{1}) = ${colVec(te1)}, \\quad T(\\mathbf{e}_{2}) = ${colVec(te2)}`,
        value: answer,
        summary: `The images of the basis vectors are the columns, in order: A = ${pmatrix(matrix)}. Row ${i + 1}, column ${j + 1} is ${minus(answer)}.`,
        hint: 'Where e₁ lands is the first column, not the first row. Check by applying your matrix to e₁.',
        steps: [
          { label: 'T(e₁) becomes the first column.', latex: `A = \\begin{pmatrix} ${at(te1, 0)} & \\cdot \\\\ ${at(te1, 1)} & \\cdot \\end{pmatrix}` },
          { label: 'T(e₂) becomes the second.', latex: `A = ${pmatrix(matrix)}` },
        ],
        traps: traps(answer, [[entryOf(matrix, j, i), 'transformation-images-written-as-rows']]),
      });
    },
  }),

  template({
    id: 'geometric-matrix-entry',
    conceptId: 'transformation-matrix',
    description: 'An entry of the matrix of a rotation or a reflection.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const CASES = [
        { name: 'rotation by 90° anticlockwise', matrix: [[0, -1], [1, 0]] },
        { name: 'rotation by 270° anticlockwise', matrix: [[0, 1], [-1, 0]] },
        { name: 'reflection in the x-axis', matrix: [[1, 0], [0, -1]] },
        { name: 'reflection in the y-axis', matrix: [[-1, 0], [0, 1]] },
        { name: 'reflection in the line y = x', matrix: [[0, 1], [1, 0]] },
        { name: 'rotation by 180°', matrix: [[-1, 0], [0, -1]] },
      ] as const;
      const chosen = rng.pick(CASES);
      const i = rng.int(0, 1);
      const j = rng.int(0, 1);
      const answer = entryOf(chosen.matrix, i, j);

      return numericExercise({
        prompt: `T is the ${chosen.name}. Find the entry in row ${i + 1}, column ${j + 1} of its matrix.`,
        value: answer,
        summary: `Track the basis vectors: e₁ goes to ${colVec(columnOf(chosen.matrix, 0))} and e₂ to ${colVec(columnOf(chosen.matrix, 1))}. Those are the columns, giving ${pmatrix(chosen.matrix)}.`,
        hint: 'Draw e₁ and e₂, apply the description to each, and write the two results as columns.',
        traps: traps(answer, [[entryOf(chosen.matrix, j, i), 'transformation-images-written-as-rows']]),
      });
    },
  }),

  template({
    id: 'composition-matrix-entry',
    conceptId: 'transformation-matrix',
    description: 'The matrix of one transformation done after another.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      let s: Mat = [vector(rng, 2, 3), vector(rng, 2, 3)];
      let t: Mat = [vector(rng, 2, 3), vector(rng, 2, 3)];
      if (JSON.stringify(matMul(s, t)) === JSON.stringify(matMul(t, s))) {
        s = [
          [2, 1],
          [0, 1],
        ];
        t = [
          [1, 0],
          [3, 1],
        ];
      }
      const composed = matMul(s, t);
      const reversed = matMul(t, s);
      const i = rng.int(0, 1);
      const j = rng.int(0, 1);
      const answer = entryOf(composed, i, j);

      return numericExercise({
        prompt: `Apply T first, then S. Find the entry in row ${i + 1}, column ${j + 1} of the matrix of the combined transformation.`,
        latex: `S = ${pmatrix(s)}, \\quad T = ${pmatrix(t)}`,
        value: answer,
        summary: `Doing T first puts its matrix on the right: the composition is ST = ${pmatrix(composed)}. Row ${i + 1}, column ${j + 1} is ${minus(answer)}.`,
        hint: 'Apply the composition to a vector x: you compute Tx first, then S of that — so S(Tx) = (ST)x.',
        traps: traps(answer, [[entryOf(reversed, i, j), 'matrix-composition-order-reversed']]),
      });
    },
  }),

  /* ------------------------------------------------------------ eigenvalues */
  template({
    id: 'characteristic-polynomial',
    conceptId: 'eigenvalues',
    description: 'Expand det(A − xI) for a 2×2 matrix.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = nonZero(rng, 5);
      const b = nonZero(rng, 5);
      const c = nonZero(rng, 5);
      let d = nonZero(rng, 5);
      // A zero trace deletes the linear term, and the sign trap with it.
      if (a + d === 0) d = d + 1 === 0 ? d + 2 : d + 1;
      const trace = a + d;
      let determinant = a * d - b * c;
      if (determinant === 0) determinant = a * d - b * c;
      const matrix: Mat = [
        [a, b],
        [c, d],
      ];
      const canonical = expr('x^2', signedTerm(-trace, 'x'), signedConstant(determinant));
      const flipped = expr('x^2', signedTerm(trace, 'x'), signedConstant(determinant));

      return expressionExercise({
        prompt: 'Expand det(A − xI) and give the characteristic polynomial in x.',
        latex: `A = ${pmatrix(matrix)}`,
        canonical,
        summary: `det(A − xI) = (${minus(a)} − x)(${minus(d)} − x) − (${minus(b)})(${minus(c)}) = x² − (${minus(trace)})x ${determinant < 0 ? '−' : '+'} ${Math.abs(determinant)}. The linear coefficient is minus the trace; the constant is the determinant.`,
        hint: 'Subtract x from each diagonal entry, then take the 2×2 determinant as usual. Both minus signs matter.',
        steps: [
          { label: 'Subtract x down the diagonal.', latex: `A - xI = \\begin{pmatrix} ${a} - x & ${b} \\\\ ${c} & ${d} - x \\end{pmatrix}` },
          { label: 'Take the determinant.', latex: `(${a} - x)(${d} - x) - (${b})(${c})` },
          { label: 'Expand and collect.', latex: `x^{2} - ${trace}x + ${determinant}` },
        ],
        traps: [trap(flipped, 'characteristic-polynomial-sign-slipped')],
      });
    },
  }),

  template({
    id: 'eigenvalue-2x2',
    conceptId: 'eigenvalues',
    description: 'The larger eigenvalue of a 2×2 matrix with whole-number eigenvalues.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const lambda1 = nonZero(rng, 5);
      const lambda2 = nonZero(rng, 5, [lambda1]);
      /*
       * A = P D P⁻¹ with P = [[1, 1], [t1, t1 + 1]] and det P = 1, so A stays
       * integral. t1 = 0 and t1 = −1 are excluded because they zero the
       * bottom-left entry, and a triangular matrix wears its eigenvalues on its
       * diagonal — which would make `eigenvalue-read-off-diagonal` the correct
       * answer and strip the one trap this item exists to set. Away from those
       * two values the top-left entry is neither eigenvalue.
       */
      const t1 = rng.pick([-3, -2, 1, 2]);
      const t2 = t1 + 1;
      const matrix: Mat = [
        [lambda1 * t2 - lambda2 * t1, lambda2 - lambda1],
        [t1 * t2 * (lambda1 - lambda2), lambda2 * t2 - lambda1 * t1],
      ];
      const larger = Math.max(lambda1, lambda2);
      const product = lambda1 * lambda2;

      return numericExercise({
        prompt: 'Find the larger eigenvalue of A.',
        latex: `A = ${pmatrix(matrix)}`,
        value: larger,
        summary: `The characteristic equation is x² − ${minus(lambda1 + lambda2)}x ${product < 0 ? '−' : '+'} ${Math.abs(product)} = 0, whose roots are ${minus(Math.min(lambda1, lambda2))} and ${minus(larger)}. The larger is ${minus(larger)}.`,
        hint: 'Solve det(A − xI) = 0. Check your pair afterwards: they must add to the trace and multiply to the determinant.',
        steps: [
          { label: 'The characteristic equation.', latex: `x^{2} - ${lambda1 + lambda2}x + ${product} = 0` },
          { label: 'Factorise.', latex: `(x - ${lambda1})(x - ${lambda2}) = 0` },
          { label: 'Take the larger root.', latex: `\\lambda = ${larger}` },
        ],
        traps: traps(larger, [
          [entryOf(matrix, 0, 0), 'eigenvalue-read-off-diagonal'],
          [product, 'eigenvalue-given-as-determinant'],
        ]),
      });
    },
  }),

  template({
    id: 'triangular-eigenvalue',
    conceptId: 'eigenvalues',
    description: 'Read an eigenvalue straight off a triangular matrix.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const d1 = nonZero(rng, 6);
      const d2 = nonZero(rng, 6, [d1]);
      const d3 = nonZero(rng, 6, [d1, d2]);
      const matrix: Mat = [
        [d1, nonZero(rng, 4), nonZero(rng, 4)],
        [0, d2, nonZero(rng, 4)],
        [0, 0, d3],
      ];
      const largest = Math.max(d1, d2, d3);

      return numericExercise({
        prompt: 'Find the largest eigenvalue of this upper triangular matrix.',
        latex: pmatrix(matrix),
        value: largest,
        summary: `For a triangular matrix det(A − xI) is the product of the diagonal entries each less x, so the eigenvalues are exactly ${minus(d1)}, ${minus(d2)} and ${minus(d3)}. The largest is ${minus(largest)}.`,
        hint: 'The determinant of a triangular matrix is the product down its diagonal — and A − xI is still triangular.',
        traps: traps(largest, [
          [d1 * d2 * d3, 'eigenvalue-given-as-determinant'],
          [d1 + d2 + d3, 'eigenvalue-given-as-determinant'],
        ]),
      });
    },
  }),

  /* ----------------------------------------------------------- eigenvectors */
  template({
    id: 'eigenvector-ratio',
    conceptId: 'eigenvectors',
    description: 'Find the direction belonging to a given eigenvalue.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const lambda1 = nonZero(rng, 5);
      const lambda2 = nonZero(rng, 5, [lambda1]);
      const t1 = rng.int(-3, 3);
      const t2 = t1 + 1;
      const matrix: Mat = [
        [lambda1 * t2 - lambda2 * t1, lambda2 - lambda1],
        [t1 * t2 * (lambda1 - lambda2), lambda2 * t2 - lambda1 * t1],
      ];
      const p = entryOf(matrix, 0, 0);
      const q = entryOf(matrix, 0, 1);
      const unshifted = q !== 0 && (-p) % q === 0 ? -p / q : null;

      return numericExercise({
        prompt: `λ = ${minus(lambda1)} is an eigenvalue of A. Its eigenvectors have the form (1, t). Find t.`,
        latex: `A = ${pmatrix(matrix)}`,
        value: t1,
        summary: `Solving (A − ${minus(lambda1)}I)x = 0 leaves one independent equation, and it gives t = ${minus(t1)}. Every non-zero multiple of (1, ${minus(t1)}) is an eigenvector for this eigenvalue.`,
        hint: 'Subtract λ from both diagonal entries first. The shifted matrix is singular by construction, which is why one equation is enough.',
        steps: [
          { label: 'Shift the diagonal.', latex: `A - ${lambda1}I = \\begin{pmatrix} ${p - lambda1} & ${q} \\\\ ${entryOf(matrix, 1, 0)} & ${entryOf(matrix, 1, 1) - lambda1} \\end{pmatrix}` },
          { label: 'The top row, with x = 1.', latex: `${p - lambda1} + ${q}t = 0` },
          { label: 'Solve for t.', latex: `t = ${t1}` },
        ],
        traps: traps(t1, unshifted === null ? [] : [[unshifted, 'eigenvector-solved-without-shift']]),
      });
    },
  }),

  template({
    id: 'not-an-eigenvector-choice',
    conceptId: 'eigenvectors',
    description: 'Spot the one vector a matrix does not merely stretch.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const lambda1 = nonZero(rng, 4);
      const lambda2 = nonZero(rng, 4, [lambda1]);
      const t1 = rng.int(-3, 2);
      const t2 = t1 + 1;
      const matrix: Mat = [
        [lambda1 * t2 - lambda2 * t1, lambda2 - lambda1],
        [t1 * t2 * (lambda1 - lambda2), lambda2 * t2 - lambda1 * t1],
      ];
      const v1 = [1, t1];
      const v2 = [1, t2];
      const scaled = [2, 2 * t1];
      let outsider = [1, t1 + 3];
      if (at(outsider, 1) === t2) outsider = [1, t1 + 4];

      return choiceExercise({
        prompt: 'Which of these is not an eigenvector of A?',
        latex: `A = ${pmatrix(matrix)}`,
        ...options(
          [
            { latex: colVec(outsider), correct: true },
            { latex: colVec(v1) },
            { latex: colVec(v2) },
            { latex: colVec(scaled), misconceptionId: 'eigenvector-scaled-taken-as-different' },
          ],
          rng.shuffle,
        ),
        summary: `A has eigenvalues ${minus(lambda1)} and ${minus(lambda2)}, with eigenvectors along (1, ${minus(t1)}) and (1, ${minus(t2)}). Any non-zero multiple of an eigenvector is an eigenvector too, so doubling one changes nothing. Only a direction that is neither of those two is left over.`,
        hint: 'Multiply each candidate by A and ask whether the result points the same way. Scaling a vector never changes its direction.',
      });
    },
  }),

  template({
    id: 'eigenspace-dimension',
    conceptId: 'eigenvectors',
    description: 'The dimension of the eigenspace of a repeated eigenvalue.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const lambda = nonZero(rng, 5);
      const mu = nonZero(rng, 5, [lambda]);
      const wide = rng.bool();
      const c = wide ? 0 : nonZero(rng, 4);
      const e = wide ? 0 : nonZero(rng, 4);
      const matrix: Mat = [
        [lambda, c, nonZero(rng, 4)],
        [0, lambda, e],
        [0, 0, mu],
      ];
      const shifted: Mat = matrix.map((row, i) => row.map((value, j) => (i === j ? value - lambda : value)));
      const answer = 3 - rankOf(shifted);

      return numericExercise({
        prompt: `λ = ${minus(lambda)} is a repeated eigenvalue of A. What is the dimension of its eigenspace?`,
        latex: `A = ${pmatrix(matrix)}`,
        value: answer,
        summary: `The eigenspace is the null space of A − ${minus(lambda)}I, which has rank ${rankOf(shifted)} — so its dimension is 3 − ${rankOf(shifted)} = ${answer}. A repeated eigenvalue does not promise a two-dimensional eigenspace; it only allows one.`,
        hint: 'Subtract λ from the diagonal and find the rank of what is left. The eigenspace is that matrix’s null space.',
        traps: traps(answer, [[2, 'eigenspace-dimension-taken-as-multiplicity']]),
      });
    },
  }),

  /* -------------------------------------------------------- diagonalization */
  template({
    id: 'diagonal-entry',
    conceptId: 'diagonalization',
    description: 'Read an entry of D off the order of the eigenvalues.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const lambda1 = nonZero(rng, 6);
      const lambda2 = nonZero(rng, 6, [lambda1]);
      // As in `eigenvalue-2x2`: 0 and −1 make A triangular, and the trap that
      // names reading D off A's diagonal would then be the right answer.
      const t1 = rng.pick([-3, -2, 1, 2]);
      const t2 = t1 + 1;
      const matrix: Mat = [
        [lambda1 * t2 - lambda2 * t1, lambda2 - lambda1],
        [t1 * t2 * (lambda1 - lambda2), lambda2 * t2 - lambda1 * t1],
      ];
      const which = rng.int(0, 1);
      const answer = which === 0 ? lambda1 : lambda2;

      return numericExercise({
        prompt: `A = PDP⁻¹, where the columns of P are eigenvectors for λ = ${minus(lambda1)} and λ = ${minus(lambda2)}, in that order. Find the entry in row ${which + 1}, column ${which + 1} of D.`,
        latex: `A = ${pmatrix(matrix)}`,
        value: answer,
        summary: `D holds the eigenvalues down its diagonal, in the same order as the eigenvectors were put into P. Column ${which + 1} of P belongs to λ = ${minus(answer)}, so that is entry (${which + 1}, ${which + 1}) of D.`,
        hint: 'D is not read off A. It holds the eigenvalues, ordered to match the columns of P.',
        traps: traps(answer, [[entryOf(matrix, which, which), 'eigenvalue-read-off-diagonal']]),
      });
    },
  }),

  template({
    id: 'diagonalizable-choice',
    conceptId: 'diagonalization',
    description: 'Spot the matrix that cannot be diagonalised.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const lambda = nonZero(rng, 5);
      const mu = nonZero(rng, 5, [lambda]);
      const shear: Mat = [
        [lambda, nonZero(rng, 4)],
        [0, lambda],
      ];
      const repeatedButFine: Mat = [
        [lambda, 0],
        [0, lambda],
      ];
      const distinct: Mat = [
        [lambda, nonZero(rng, 4)],
        [0, mu],
      ];
      const symmetric: Mat = [
        [lambda, 2],
        [2, mu],
      ];

      return choiceExercise({
        prompt: 'Which of these matrices cannot be diagonalised?',
        ...options(
          [
            { latex: pmatrix(shear), correct: true },
            { latex: pmatrix(repeatedButFine), misconceptionId: 'repeated-eigenvalue-assumed-fatal' },
            { latex: pmatrix(distinct) },
            { latex: pmatrix(symmetric) },
          ],
          rng.shuffle,
        ),
        summary: `All four have real eigenvalues, and two of them repeat one. Repetition is not the test: the scalar matrix is already diagonal, while the shear has only a one-dimensional eigenspace for its repeated eigenvalue — one eigenvector where two are needed, so no basis of eigenvectors exists.`,
        hint: 'Count independent eigenvectors, not eigenvalues. A 2×2 matrix is diagonalisable exactly when it has two of them.',
      });
    },
  }),

  template({
    id: 'matrix-power-entry',
    conceptId: 'diagonalization',
    description: 'An entry of a matrix power, computed through the diagonal form.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const lambda1 = rng.int(1, 3);
      const lambda2 = nonZero(rng, 3, [lambda1]);
      const t1 = rng.int(-2, 1);
      const t2 = t1 + 1;
      const matrix: Mat = [
        [lambda1 * t2 - lambda2 * t1, lambda2 - lambda1],
        [t1 * t2 * (lambda1 - lambda2), lambda2 * t2 - lambda1 * t1],
      ];
      const power = rng.int(2, 3);
      let result = matrix;
      for (let step = 1; step < power; step += 1) result = matMul(result, matrix);
      const i = rng.int(0, 1);
      const j = rng.int(0, 1);
      const answer = entryOf(result, i, j);

      return numericExercise({
        prompt: `Find the entry in row ${i + 1}, column ${j + 1} of A^${power}.`,
        latex: `A = ${pmatrix(matrix)}`,
        value: answer,
        summary: `A = PDP⁻¹ with eigenvalues ${minus(lambda1)} and ${minus(lambda2)}, so A^${power} = PD^${power}P⁻¹ — the eigenvalues are raised to the power and nothing else changes. The result is ${pmatrix(result)}.`,
        hint: 'Either multiply A by itself, or diagonalise and raise the eigenvalues. Raising the individual entries is neither.',
        traps: traps(answer, [[entryOf(matrix, i, j) ** power, 'matrix-power-taken-entrywise']]),
      });
    },
  }),

  template({
    id: 'diagonalization-form-choice',
    conceptId: 'diagonalization',
    description: 'Which way round P and its inverse go.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const lambda1 = nonZero(rng, 5);
      const lambda2 = nonZero(rng, 5, [lambda1]);

      return choiceExercise({
        prompt: 'P has the eigenvectors of A as its columns and D holds the matching eigenvalues. Which equation is correct?',
        latex: `D = ${pmatrix([
          [lambda1, 0],
          [0, lambda2],
        ])}`,
        ...options(
          [
            { latex: 'A = PDP^{-1}', correct: true },
            { latex: 'A = P^{-1}DP', misconceptionId: 'diagonalization-factors-swapped' },
            { latex: 'A = PDP^{T}' },
            { latex: 'A = DPD^{-1}' },
          ],
          rng.shuffle,
        ),
        summary:
          'Read it right to left as a journey: P⁻¹ rewrites a vector in the eigenvector basis, D scales each eigen-direction, and P translates back. The equivalent form D = P⁻¹AP says the same thing rearranged.',
        hint: 'Apply the right-hand side to an eigenvector of A and check that you get the eigenvalue times it.',
      });
    },
  }),

  /* ------------------------------------------------ eigenvalues at work */
  template({
    id: 'long-run-growth-factor',
    conceptId: 'eigen-applications',
    description: 'Which eigenvalue sets the long-run growth.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const dominant = rng.int(2, 5);
      const other = rng.pick([-1, 1, 0]) === 0 ? 1 : rng.int(-1, 1) === 0 ? 1 : rng.int(1, dominant - 1);
      const small = Math.min(other === dominant ? 1 : other, dominant - 1);

      return numericExercise({
        prompt: `A population vector is multiplied by A each year. A has eigenvalues ${dominant} and ${small}. By what factor does the population grow each year in the long run?`,
        value: dominant,
        summary: `Write the starting vector in the eigenvector basis. After n years the two parts have been multiplied by ${dominant}ⁿ and ${small}ⁿ, and the first swamps the second — so the growth factor settles at ${dominant}.`,
        hint: 'After many steps, only the largest eigenvalue in size still matters. The other part has been scaled into insignificance.',
        steps: [
          { label: 'Split the start into eigen-directions.', latex: `\\mathbf{x}_{0} = c_{1}\\mathbf{v}_{1} + c_{2}\\mathbf{v}_{2}` },
          { label: 'Each direction scales by its own eigenvalue.', latex: `\\mathbf{x}_{n} = c_{1}(${dominant})^{n}\\mathbf{v}_{1} + c_{2}(${small})^{n}\\mathbf{v}_{2}` },
          { label: 'The larger one dominates.', latex: `\\mathbf{x}_{n} \\approx c_{1}(${dominant})^{n}\\mathbf{v}_{1}` },
        ],
        traps: traps(dominant, [
          [small, 'dominant-eigenvalue-not-identified'],
          [dominant + small, 'dominant-eigenvalue-not-identified'],
        ]),
      });
    },
  }),

  template({
    id: 'steady-state-share',
    conceptId: 'eigen-applications',
    description: 'The long-run share of a two-state process.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const leave = rng.pick([10, 20, 25, 30, 40, 50]);
      const arrive = rng.pick([10, 20, 25, 30, 40, 50].filter((value) => value !== leave));
      const total = leave + arrive;

      return fractionExercise({
        prompt: `Each year ${leave}% of city dwellers move to the country and ${arrive}% of country dwellers move to the city. In the long run, what fraction of the population lives in the city?`,
        value: fraction(arrive, total),
        requireSimplified: true,
        summary: `The steady state is the eigenvector for eigenvalue 1, which is proportional to (${arrive}, ${leave}). Normalising so the two shares add to the whole gives a city share of ${arrive}/${total}.`,
        hint: 'In the long run the two flows balance: the number leaving the city equals the number arriving. Write that as one equation and normalise.',
        steps: [
          { label: 'Balance the flows.', latex: `${leave}c = ${arrive}k`, note: 'c is the city share, k the country share.' },
          { label: 'The two shares make up the whole.', latex: 'c + k = 1' },
          { label: 'Solve.', latex: `c = \\frac{${arrive}}{${total}}` },
        ],
        traps: [trap(`${arrive}/${leave}`, 'steady-state-not-normalised')],
      });
    },
  }),

  template({
    id: 'eigenvector-after-steps',
    conceptId: 'eigen-applications',
    description: 'Where an eigenvector lands after repeated application.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const lambda = rng.int(2, 4);
      const steps = rng.int(3, 5);
      const v = [nonZero(rng, 4), nonZero(rng, 4)];
      const i = rng.int(0, 1);
      const answer = lambda ** steps * at(v, i);

      return numericExercise({
        prompt: `v is an eigenvector of A with eigenvalue ${lambda}. Find the ${ordinal(i)} component of A^${steps}v.`,
        latex: `\\mathbf{v} = ${colVec(v)}`,
        value: answer,
        summary: `Each application multiplies v by ${lambda}, so ${steps} of them multiply it by ${lambda}^${steps} = ${lambda ** steps}. The ${ordinal(i)} component becomes ${lambda ** steps} × ${minus(at(v, i))} = ${minus(answer)}.`,
        hint: 'An eigenvector never changes direction, so applying A repeatedly is just repeated scaling — no matrix multiplication needed.',
        traps: traps(answer, [
          [lambda * at(v, i), 'matrix-power-taken-entrywise'],
          [lambda * steps * at(v, i), 'matrix-power-taken-entrywise'],
        ]),
      });
    },
  }),
];

/**
 * The body of `inverse-choice`, lifted out so the template can pick its
 * parameters and then hand off with a matrix it has already checked.
 *
 * The determinant is deliberately not ±1 here: the whole point of the item is
 * the division, and a determinant of 1 makes the adjugate and the inverse the
 * same matrix, leaving two identical choices.
 */
function buildInverseChoice(rng: Rng, matrix: Mat) {
  const p = entryOf(matrix, 0, 0);
  const q = entryOf(matrix, 0, 1);
  const r = entryOf(matrix, 1, 0);
  const s = entryOf(matrix, 1, 1);
  const determinant = p * s - q * r;
  const adjugate: Mat = [
    [s, -q],
    [-r, p],
  ];
  const signsWrong: Mat = [
    [-s, q],
    [r, -p],
  ];
  const scaled = (m: Mat): string => `\\frac{1}{${determinant}}${pmatrix(m)}`;

  return choiceExercise({
    prompt: 'Which matrix is the inverse of A?',
    latex: `A = ${pmatrix(matrix)}`,
    ...options(
      [
        { latex: scaled(adjugate), correct: true },
        { latex: pmatrix(adjugate), misconceptionId: 'inverse-determinant-not-divided' },
        { latex: scaled(signsWrong), misconceptionId: 'inverse-adjugate-signs-misplaced' },
        { latex: pmatrix(matrix) },
      ],
      rng.shuffle,
    ),
    summary: `Swap the diagonal entries, negate the other two, and divide the whole thing by det A = ${minus(determinant)}. Skipping the division gives a matrix whose product with A is det(A)·I rather than I.`,
    hint: 'Three moves: swap the diagonal, negate the off-diagonal, divide by the determinant. Missing the last one is the usual slip.',
  });
}
